import React, { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import "@/App.css";
import "@/index.css";
import Shell, { Protected } from "@/components/Shell";
import { ItemCard } from "@/components/ItemCard";
import { api, saveAuth, getUser, rarityClass, playSound, exactValue } from "@/api";
import PlayerSearch from "@/components/PlayerSearch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Search, Upload, Plus, Trash2, ShieldAlert, Send, Crown, ScanLine, Calculator as CalcIcon, MessageSquare, X, Copy, Flag, Clipboard, ArrowLeftRight, Filter, Pencil, Megaphone, Eye, LifeBuoy, Bot, UserRound, CheckCircle, Star, Clock, TrendingUp, Shield, Award, Users, ShoppingBag, Lock, Bug, AlertTriangle, Loader2, Mail, ChevronRight, Archive, RefreshCw, Handshake, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import TutorialOverlay from "@/components/TutorialOverlay";
import AuthPrompt from "@/components/AuthPrompt";
import AdSlot from "@/components/AdSlot";

// =================================================================================
// Cooldown hook — prevents spam clicks
// =================================================================================
function useCooldown(ms = 2000) {
  const [cd, setCd] = useState({});
  const timers = useRef({});
  const isOnCooldown = (key) => !!cd[key];
  const trigger = (key) => {
    setCd(c => ({...c, [key]: true}));
    timers.current[key] = setTimeout(() => setCd(c => ({...c, [key]: false})), ms);
  };
  useEffect(() => () => { Object.values(timers.current).forEach(clearTimeout); }, []);
  return { isOnCooldown, trigger };
}

// =================================================================================
// Home
// =================================================================================
const TRADING_QUOTES = [
  "Trade smarter, not harder.",
  "Know the value before you trade.",
  "A fair trade is a happy trade.",
  "Don't get scammed — get informed.",
  "Your pets are worth knowing.",
  "Scammers hate this one trick: knowledge.",
  "Every trade tells a story. Make it a win.",
  "Trust the numbers, not the promises.",
  "Good trades come to those who prepare.",
  "The best traders never stop learning.",
  "Value is power. Use it wisely.",
  "A win-win trade is the only real win.",
  "Knowledge is the rarest pet of all.",
  "Trade with confidence. Trade with data.",
  "Your inventory has a voice. Listen to it.",
];

function Home() {
  const user = getUser();
  const [ads, setAds] = useState([]);
  const [quoteIdx, setQuoteIdx] = useState(0);
  useEffect(() => {
    api.get("/ads", { params: { placement: "home" } }).then(r => setAds(r.data.ads)).catch(() => {});
    setQuoteIdx(Math.floor(Math.random() * TRADING_QUOTES.length));
    const t = setInterval(() => setQuoteIdx(i => (i + 1) % TRADING_QUOTES.length), 8000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="space-y-12">
      <section className="pop-in card-cartoon p-8 md:p-14 text-center relative overflow-hidden">
        <div className="absolute -top-8 -left-8 w-40 h-40 bg-[#ffd1e8] rounded-full floaty" />
        <div className="absolute -bottom-12 -right-8 w-56 h-56 bg-[#c8efff] rounded-full floaty-2" />
        <div className="relative">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2a1f3d]">Trade smarter.</h1>
          <p key={quoteIdx} className="mt-3 text-lg text-[#ff5a8a] font-display italic min-h-[2rem] animate-fadeIn">{TRADING_QUOTES[quoteIdx]}</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/calculator"><Button data-testid="cta-calculator" className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a] text-white shadow-cartoon btn-pop"><CalcIcon className="w-4 h-4 mr-2"/>Calculator</Button></Link>
            <Link to="/scanner"><Button data-testid="cta-scanner" className="rounded-2xl bg-[#7649e6] hover:bg-[#5d35c4] text-white shadow-cartoon btn-pop"><ScanLine className="w-4 h-4 mr-2"/>AI Scanner</Button></Link>
            <Link to="/trades"><Button data-testid="cta-trades" className="rounded-2xl bg-[#ffb13b] hover:bg-[#f59f1f] text-white shadow-cartoon btn-pop"><MessageSquare className="w-4 h-4 mr-2"/>Trade Center</Button></Link>
          </div>
          {!user && <p className="mt-4 text-sm text-[#7649e6]"><Link to="/login" className="underline font-bold">Verify with Roblox</Link> to unlock chat, trades, and scanner.</p>}
        </div>
      </section>
      {ads.length > 0 && <section className="grid md:grid-cols-2 gap-4">
        {ads.map(a => <a key={a.id} href={a.link_url || "#"} target="_blank" rel="noreferrer" className="card-cartoon p-4 flex items-center gap-4 btn-pop hover:shadow-cartoon-lg">
          {a.image_url && <img src={a.image_url} alt={a.title} className="w-20 h-20 rounded-2xl object-cover"/>}
          <div><div className="text-xs uppercase text-[#7649e6]">Sponsored{a.sponsor ? ` • ${a.sponsor}` : ""}</div><div className="font-display text-lg">{a.title}</div></div>
        </a>)}
      </section>}
      <AdSlot placement="home_bottom"/>

      {/* Safety Reminder */}
      <section className="card-cartoon p-6 md:p-8 border-l-4 border-[#ff5a8a] bg-gradient-to-r from-[#fff5f7] to-white pop-in">
        <div className="flex items-start gap-4">
          <div className="shrink-0 mt-1">
            <svg className="w-10 h-10 text-[#7649e6]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <div>
            <h2 className="font-display text-xl text-[#2a1f3d] mb-2">Friendly Reminder</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              We will <strong className="text-[#ff5a8a]">never</strong> ask for your Roblox password, email, or personal information.
              No staff member or moderator will ever request your login credentials. 
              Keep your account safe — <strong className="text-[#7649e6]">never share your password</strong> with anyone, including people claiming to be staff.
            </p>
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              <span className="inline-flex items-center gap-1.5 text-[#36a86b]">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Never share passwords
              </span>
              <span className="inline-flex items-center gap-1.5 text-[#36a86b]">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Report suspicious users
              </span>
              <span className="inline-flex items-center gap-1.5 text-[#36a86b]">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Trade safely
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Rules & Terms */}
      <section className="grid md:grid-cols-2 gap-6 pop-in">
        <div className="card-cartoon p-6 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-[#ff5a8a]" />
            <h2 className="font-display text-2xl text-[#2a1f3d]">Community Rules</h2>
          </div>
          <ul className="space-y-2.5 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-[#ff5a8a] font-bold mt-0.5">01.</span>
              <span><strong>No scamming</strong> — Attempting to scam, trick, or deceive other users will result in an immediate permanent ban.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#ff5a8a] font-bold mt-0.5">02.</span>
              <span><strong>No off-platform trading</strong> — Do not advertise or conduct trades outside of Roblox (PayPal, CashApp, gift cards, etc.).</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#ff5a8a] font-bold mt-0.5">03.</span>
              <span><strong>No harassment</strong> — Hate speech, bullying, threats, or toxic behavior of any kind is not tolerated.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#ff5a8a] font-bold mt-0.5">04.</span>
              <span><strong>No spam or ads</strong> — Do not spam messages, post repetitive content, or advertise external services.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#ff5a8a] font-bold mt-0.5">05.</span>
              <span><strong>No phishing</strong> — Do not ask for passwords, login info, or attempt to trick users into revealing credentials.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#ff5a8a] font-bold mt-0.5">06.</span>
              <span><strong>Keep your bio appropriate</strong> — Fraudulent, misleading, or scam-related content in your Roblox bio will be flagged and may result in a ban.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#ff5a8a] font-bold mt-0.5">07.</span>
              <span><strong>One account per person</strong> — Multiple accounts used to evade bans or manipulate trades are prohibited.</span>
            </li>
          </ul>
        </div>

        <div className="card-cartoon p-6 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-[#7649e6]" />
            <h2 className="font-display text-2xl text-[#2a1f3d]">Terms of Service</h2>
          </div>
          <ul className="space-y-2.5 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-[#7649e6] font-bold mt-0.5">i.</span>
              <span>This site is a <strong>fan project</strong> and is not affiliated with Roblox Corporation or Uplift Games (Adopt Me). All pet images and names are property of their respective owners.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#7649e6] font-bold mt-0.5">ii.</span>
              <span>The AI scanner is provided <strong>"as is"</strong> with no guarantee of accuracy. Values are estimates and may not reflect actual market prices. Always double-check before trading.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#7649e6] font-bold mt-0.5">iii.</span>
              <span>We reserve the right to <strong>ban, suspend, or restrict</strong> any account at our discretion for violating rules or terms.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#7649e6] font-bold mt-0.5">iv.</span>
              <span>By using this site, you confirm you are <strong>13 years or older</strong>. If you are under 13, please do not use this service.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#7649e6] font-bold mt-0.5">v.</span>
              <span>We store your <strong>Roblox username and user ID</strong> to provide verification and trading features. No passwords are ever collected.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#7649e6] font-bold mt-0.5">vi.</span>
              <span>We are not responsible for any <strong>losses</strong> resulting from trades made using this platform. Trade responsibly.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#7649e6] font-bold mt-0.5">vii.</span>
              <span>These terms may be <strong>updated at any time</strong>. Continued use after changes constitutes acceptance of the new terms.</span>
            </li>
          </ul>
        </div>
      </section>

      <div className="text-center text-[10px] text-gray-400 pb-4">
        Last updated: June 2026. AdptMeValues is a fan-made tool. Not affiliated with Roblox or Adopt Me.
      </div>
    </div>
  );
}

// =================================================================================
// Login
// =================================================================================
function Login() {
  const [u, setU] = useState(""); const [code, setCode] = useState(null); const [loading, setLoading] = useState(false);
  const [logCooldown, setLogCooldown] = useState(false);
  const nav = useNavigate();
  const start = async () => {
    if (logCooldown) return;
    setLogCooldown(true);
    setTimeout(() => setLogCooldown(false), 3000);
    setLoading(true);
    try { const r = await api.post("/auth/verify/start", { roblox_username: u }); setCode(r.data); playSound("ding"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); playSound("error"); }
    finally { setLoading(false); }
  };
  const check = async () => {
    if (logCooldown) return;
    setLogCooldown(true);
    setTimeout(() => setLogCooldown(false), 3000);
    setLoading(true);
    try { const r = await api.post("/auth/verify/check", { roblox_username: u }); saveAuth(r.data.token, r.data.user); sessionStorage.setItem("adopt_show_tutorial", "1"); toast.success(`Welcome ${r.data.user.roblox_username}!`); playSound("success"); nav("/"); window.location.reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); playSound("error"); }
    finally { setLoading(false); }
  };
  return (
    <div className="max-w-xl mx-auto pop-in"><div className="card-cartoon p-8">
      <h1 className="font-display text-4xl text-[#ff5a8a] mb-1">Roblox Bio Verification</h1>
      <p className="text-sm mb-6">No passwords. We verify by checking your Roblox profile bio.</p>
      {!code ? (
        <div className="space-y-3">
          <label className="text-sm font-semibold">Roblox username</label>
          <Input data-testid="roblox-username-input" value={u} onChange={(e) => setU(e.target.value)} placeholder="e.g. dadodadopilo" className="rounded-2xl" />
          <Button data-testid="start-verify-button" onClick={start} disabled={!u || loading} className="rounded-2xl w-full bg-[#ff6fa3] hover:bg-[#ff5a8a] shadow-cartoon btn-pop">{loading ? "Looking up…" : "Generate Verification Code"}</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="card-cartoon p-4 bg-gradient-to-br from-[#fff1a8] to-[#ffd1e8]">
            <div className="text-sm">Place this code in your Roblox "About me":</div>
            <div className="flex items-center gap-3 mt-2">
              <code data-testid="verify-code" className="font-display text-3xl text-[#7649e6] flex-1 break-all">{code.code}</code>
              <button data-testid="copy-code" onClick={() => { navigator.clipboard.writeText(code.code); toast.success("Copied!"); }} className="btn-pop"><Copy className="w-5 h-5"/></button>
            </div>
            <div className="text-xs mt-2 text-[#7649e6]">Expires in 10 minutes. Linked to: <b>{code.display_name}</b></div>
          </div>
          <ol className="text-sm space-y-1 list-decimal pl-5">
            <li>Open <a className="underline text-[#7649e6]" target="_blank" rel="noreferrer" href={`https://www.roblox.com/users/${code.roblox_user_id}/profile`}>your Roblox profile</a></li>
            <li>Click "Edit" on your About section, paste the code, save</li>
            <li>Click "I added it!" below</li>
          </ol>
          <div className="flex gap-2">
            <Button data-testid="check-verify-button" onClick={check} disabled={loading} className="rounded-2xl flex-1 bg-[#7649e6] hover:bg-[#5d35c4] shadow-cartoon btn-pop">{loading ? "Checking…" : "I added it!"}</Button>
            <Button data-testid="restart-verify-button" variant="ghost" onClick={() => setCode(null)} className="rounded-2xl">Restart</Button>
          </div>
        </div>
      )}
    </div></div>
  );
}

// =================================================================================
// Variant + Potion picker for pets
// =================================================================================
function VariantPicker({ item, value, onChange }) {
  // value = { variant: 'normal'|'neon'|'mega', potion: 'none'|'fly'|'ride'|'flyride' }
  if (!item || item.type !== "pets") return null;
  const hasFly = value.potion === "fly" || value.potion === "flyride";
  const hasRide = value.potion === "ride" || value.potion === "flyride";
  const togglePotion = (kind) => {
    let f = hasFly, r = hasRide;
    if (kind === "fly") f = !f;
    else if (kind === "ride") r = !r;
    else if (kind === "dual") { const both = f && r; f = !both; r = !both; }
    const p = f && r ? "flyride" : f ? "fly" : r ? "ride" : "none";
    onChange({ ...value, potion: p });
  };
  const setVariant = (v) => {
    const newVariant = value.variant === v ? "normal" : v;
    // When activating Neon/Mega, default potion to flyride (most common assumption)
    const newPotion = (newVariant !== "normal" && value.potion === "none") ? "flyride" : value.potion;
    onChange({ ...value, variant: newVariant, potion: newPotion });
  };
  const Btn = ({ label, active, color, onClick, testId }) => (
    <button data-testid={testId} onClick={(e) => { e.stopPropagation(); onClick(); playSound("pop"); }}
      className={`px-4 py-2 rounded-full font-display text-sm transition-all btn-pop ${active ? `text-white shadow-cartoon-sm ${color}` : "bg-white border-2 border-[#2a1f3d]/20 text-[#2a1f3d]/60"}`}>{label}</button>
  );
  return (
    <div className="flex flex-wrap gap-2 justify-center" data-testid="variant-picker">
      <Btn label="F" testId="potion-fly" color="bg-[#3aa8ff]" active={hasFly} onClick={() => togglePotion("fly")} />
      <Btn label="R" testId="potion-ride" color="bg-[#ff5a8a]" active={hasRide} onClick={() => togglePotion("ride")} />
      <Btn label="D" testId="potion-dual" color="bg-[#ff8c42]" active={hasFly && hasRide} onClick={() => togglePotion("dual")} />
      <div className="w-px bg-[#2a1f3d]/20 mx-1"/>
      <Btn label="N" testId="variant-neon" color="bg-[#a3e635]" active={value.variant === "neon"} onClick={() => setVariant("neon")} />
      <Btn label="M" testId="variant-mega" color="bg-[#c279ff]" active={value.variant === "mega"} onClick={() => setVariant("mega")} />
    </div>
  );
}

// Compute exact value using per-potion fields (matches backend _value_for)
// =================================================================================
// Item Picker Dialog (with variant picker for pets)
// =================================================================================
function ItemPickerDialog({ open, onOpenChange, onPick, petsOnly = false }) {
  const [q, setQ] = useState(""); const [items, setItems] = useState([]); const [type, setType] = useState(petsOnly ? "pets" : "");
  const [selected, setSelected] = useState(null);
  const [variant, setVariant] = useState({ variant: "normal", potion: "flyride" });
  const [quantity, setQuantity] = useState(1);
  useEffect(() => { if (!open) return; const t = setTimeout(() => api.get("/items", { params: { search: q, type, limit: 60 } }).then(r => setItems(r.data.items)), 200); return () => clearTimeout(t); }, [q, type, open]);
  useEffect(() => { if (!open) { setSelected(null); setVariant({ variant: "normal", potion: "flyride" }); setQuantity(1); } }, [open]);
  const handleConfirm = () => {
    if (!selected) return;
    onPick({ ...selected, ...variant, quantity });
    setSelected(null);
    setVariant({ variant: "normal", potion: "flyride" });
    setQuantity(1);
    playSound("ding");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="item-picker-dialog" className="rounded-3xl max-w-3xl bg-white border-4 border-[#ff6fa3]/30">
        <DialogHeader><DialogTitle className="font-display text-3xl text-[#ff5a8a]">{selected ? `Customize ${selected.name}` : "Choose an item"}</DialogTitle></DialogHeader>
        {!selected ? (<>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1"><Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"/><Input data-testid="picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pets/items…" className="rounded-2xl pl-9"/></div>
            {!petsOnly && <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="picker-type-select" className="rounded-2xl w-40"><SelectValue placeholder="Type"/></SelectTrigger>
              <SelectContent><SelectItem value="all">All types</SelectItem><SelectItem value="pets">Pets</SelectItem><SelectItem value="pet wear">Pet Wear</SelectItem><SelectItem value="vehicles">Vehicles</SelectItem><SelectItem value="strollers">Strollers</SelectItem><SelectItem value="food">Food</SelectItem><SelectItem value="toys">Toys</SelectItem><SelectItem value="eggs">Eggs</SelectItem></SelectContent>
            </Select>}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 gap-2 max-h-[50vh] overflow-auto scrollbar-thin pr-1">
            {items.map(it => <ItemCard key={it.id} item={it} compact onClick={() => { setSelected(it); playSound("pop"); }} />)}
          </div>
        </>) : (<>
          <div className="flex flex-col items-center gap-3 p-4">
            <ItemCard item={selected}/>
            {selected.type === "pets" ? (
              <>
                <p className="text-xs text-center text-[#7649e6]">Customize potions & variant. Tap to toggle.</p>
                <VariantPicker item={selected} value={variant} onChange={setVariant}/>
                <div className="text-sm">Value: <b>{exactValue(selected, variant.variant, variant.potion).toFixed(2)}</b></div>
              </>
            ) : <p className="text-sm text-[#7649e6]">This item type cannot be made Neon or Mega.</p>}
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs text-gray-500">Qty:</span>
                  <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-7 h-7 rounded-full bg-red-100 text-red-600 font-bold text-sm hover:bg-red-200 transition-colors">-</button>
                  <input value={quantity} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) setQuantity(Math.max(1, Math.min(99, v))); }} className="w-14 h-7 text-sm font-bold text-[#7649e6] text-center border border-[#7649e6]/30 rounded-lg bg-white outline-none" />
                  <button type="button" onClick={() => setQuantity(Math.min(99, quantity + 1))} className="w-7 h-7 rounded-full bg-green-100 text-green-600 font-bold text-sm hover:bg-green-200 transition-colors">+</button>
                </div>
              </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setSelected(null)} className="rounded-2xl">Back</Button>
            <Button data-testid="confirm-pick" onClick={handleConfirm} className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a]">Add</Button>
          </DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
}

// =================================================================================
// Calculator (slot grid)
// =================================================================================
const MIN_SLOTS = 6;
const MAX_SLOTS = 18;
function Calculator() {
  const [a, setA] = useState(Array(MIN_SLOTS).fill(null));
  const [b, setB] = useState(Array(MIN_SLOTS).fill(null));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [editing, setEditing] = useState(null);
  const [guestPrompt, setGuestPrompt] = useState(false);
  const user = getUser();

  const requireAuth = (fn) => (user ? fn() : setGuestPrompt(true));
  const openPick = (side, idx) => requireAuth(() => { setTarget({ side, idx }); setPickerOpen(true); });
  const handlePick = (it) => {
    if (!target) return;
    const side = target.side;
    const arr = side === "a" ? [...a] : [...b];
    const filled = arr.filter(Boolean).length;
    const qty = it.quantity || 1;
    if (filled + qty > MAX_SLOTS) { toast.error(`Adding ${qty} would exceed max ${MAX_SLOTS} items`); return; }
    const item = { ...it, variant: it.variant || "normal", potion: it.potion || (it.type === "pets" ? "flyride" : "none") };
    for (let n = 0; n < qty; n++) {
      let idx = arr.findIndex(x => x === null);
      if (idx === -1) {
        if (arr.length >= MAX_SLOTS) break;
        idx = arr.length;
        arr.push(null);
      }
      arr[idx] = { ...item };
    }
    if (arr.length < MAX_SLOTS && !arr.includes(null)) arr.push(null);
    side === "a" ? setA(arr) : setB(arr);
  };
  const updateVariant = (side, idx, v) => {
    const arr = side === "a" ? [...a] : [...b];
    arr[idx] = { ...arr[idx], ...v };
    side === "a" ? setA(arr) : setB(arr);
  };
  const remove = (side, idx) => {
    const arr = side === "a" ? [...a] : [...b];
    arr.splice(idx, 1);
    arr.push(null);
    side === "a" ? setA(arr) : setB(arr);
  };
  const reset = () => { setA(Array(MIN_SLOTS).fill(null)); setB(Array(MIN_SLOTS).fill(null)); };
  const swap = () => { setA(b); setB(a); };
  const calcValue = (it) => it ? exactValue(it, it.variant, it.potion) : 0;
  const totalCount = (arr) => arr.filter(Boolean).length;
  const totalSide = (arr) => arr.filter(Boolean).reduce((s, it) => s + calcValue(it), 0);
  const aTotal = totalSide(a); const bTotal = totalSide(b); const diff = bTotal - aTotal;

  const Slot = ({ side, idx, item }) => (
    <div className="relative">
      <button
        data-testid={`slot-${side}-${idx}`}
        onClick={() => item ? requireAuth(() => setEditing({ side, idx })) : openPick(side, idx)}
        className={`relative aspect-square w-full rounded-2xl border-2 transition-all ${item ? "border-[#ff6fa3] bg-white shadow-cartoon-sm" : "border-dashed border-[#ff6fa3]/40 bg-[#ffd1e8]/30 hover:bg-[#ffd1e8]/60 btn-pop"}`}
      >
        {item ? (
          <>
            <img src={item.image} alt={item.name} className={`w-full h-full object-contain p-1 rounded-2xl ${rarityClass(item.rarity)}`} onError={(e) => e.target.style.display = "none"}/>
            <div className="absolute bottom-0 inset-x-0 bg-white/90 text-[10px] font-bold truncate px-1 rounded-b-2xl">{item.name}</div>
            {item.variant !== "normal" && <span className="absolute top-1 left-1 bg-[#7649e6] text-white text-[9px] uppercase rounded-full px-1.5 py-0.5">{item.variant === "neon" ? "N" : "M"}</span>}
            {item.type === "pets" && item.potion && item.potion !== "none" && <span className="absolute top-1 right-1 bg-[#3aa8ff] text-white text-[9px] uppercase rounded-full px-1.5 py-0.5">{item.potion === "flyride" ? "FR" : item.potion === "fly" ? "F" : "R"}</span>}
          </>
        ) : (
          <Plus className="w-8 h-8 mx-auto text-[#ff6fa3]/70"/>
        )}
      </button>
      {item && <button data-testid={`remove-${side}-${idx}`} onClick={(e) => { e.stopPropagation(); requireAuth(() => remove(side, idx)); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-cartoon-sm z-10"><X className="w-3 h-3"/></button>}
    </div>
  );

  const Side = ({ side, arr, label, total }) => {
    const cnt = totalCount(arr);
    return (
    <div data-testid={`calc-side-${side}`} className="card-cartoon p-3 bg-gradient-to-br from-white to-[#ffd1e8]/30">
      <div className="flex justify-between items-center mb-2">
        <span className="font-display text-lg">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">{cnt}/{MAX_SLOTS}</span>
          <Badge data-testid={`total-${side}`} className="bg-[#ff6fa3] text-white font-display text-base">{total.toFixed(1)}</Badge>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 border-4 border-[#ff6fa3] rounded-2xl p-2 bg-[#fff7e8]">
        {arr.map((it, i) => <Slot key={i} side={side} idx={i} item={it}/>)}
      </div>
    </div>
    );
  };

  const editItem = editing ? (editing.side === "a" ? a : b)[editing.idx] : null;

  return (
    <div className="space-y-4">
      <AdSlot placement="calculator_top"/>
      <div className="card-cartoon p-4 flex flex-wrap gap-2 items-center">
        <h1 className="font-display text-3xl text-[#ff5a8a]">Value Calculator</h1>
        <div className="ml-auto flex gap-2">
          <Button data-testid="swap-button" variant="outline" onClick={() => requireAuth(swap)} className="rounded-2xl btn-pop"><ArrowLeftRight className="w-4 h-4 mr-1"/>Swap</Button>
          <Button data-testid="reset-button" variant="outline" onClick={() => requireAuth(reset)} className="rounded-2xl btn-pop"><Trash2 className="w-4 h-4 mr-1"/>Reset</Button>
        </div>
      </div>
      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <Side side="a" arr={a} label="Your Offer" total={aTotal}/>
        <div className="text-center">
          <div className="font-display text-5xl" style={{color: diff === 0 ? "#2a1f3d" : diff > 0 ? "#36a86b" : "#ff5a8a"}}>{diff > 0 ? "+" : ""}{diff.toFixed(1)}</div>
          <div className="text-xs uppercase mt-1">Difference</div>
        </div>
        <Side side="b" arr={b} label="Their Offer" total={bTotal}/>
      </div>
      <div className="mt-4">
        {a.some(Boolean) || b.some(Boolean) ? (
          <div data-testid="calc-verdict" className={`card-cartoon p-5 pop-in text-center border-4 ${diff > 0 ? "border-[#36a68b] bg-[#e8faf0]" : diff < 0 ? "border-[#ff5a8a] bg-[#ffe8ee]" : "border-[#ffb13b] bg-[#fff7e8]"}`}>
            <div className="font-display text-4xl" style={{color: diff > 0 ? "#36a68b" : diff < 0 ? "#ff5a8a" : "#ffb13b"}}>
              {diff > 0 ? "WIN" : diff < 0 ? "LOSS" : "FAIR"}
            </div>
            <div className="text-2xl font-bold mt-1" style={{color: diff > 0 ? "#36a68b" : diff < 0 ? "#ff5a8a" : "#ffb13b"}}>
              {diff > 0 ? "+" : ""}{diff.toFixed(1)}
            </div>
          </div>
        ) : (
          <div className="card-cartoon p-5 text-center text-[#7649e6]/60 font-display text-lg border-2 border-dashed border-[#7649e6]/30">
            Add items to both sides to see the result
          </div>
        )}
      </div>
      <p className="text-xs text-center text-[#7649e6]">Tip: tap a filled pet slot to set Fly/Ride/Dual potions or Neon/Mega.</p>
      <ItemPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} onPick={handlePick}/>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-3xl bg-white border-4 border-[#ff6fa3]/30 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#ff5a8a]">{editItem?.name}</DialogTitle></DialogHeader>
          {editItem && <>
            <div className="flex flex-col items-center gap-3 py-3">
              <ItemCard item={editItem}/>
              {editItem.type === "pets" ? (
                <VariantPicker item={editItem} value={{ variant: editItem.variant, potion: editItem.potion }} onChange={(v) => updateVariant(editing.side, editing.idx, v)}/>
              ) : <p className="text-sm text-[#7649e6]">This item type cannot be Neon/Mega.</p>}
              <div className="text-sm">Value: <b>{calcValue(editItem).toFixed(2)}</b></div>
            </div>
            <DialogFooter><Button variant="destructive" onClick={() => { remove(editing.side, editing.idx); setEditing(null); }} className="rounded-2xl">Remove</Button><Button onClick={() => setEditing(null)} className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a]">Done</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
      <AuthPrompt
        open={guestPrompt}
        onOpenChange={setGuestPrompt}
        title="Sign in to use the calculator"
        description="Sign in with Roblox to use the calculator safely and find your best trades."
      />
    </div>
  );
}

// =================================================================================
// Scanner
// =================================================================================
function Scanner() {
  const [file, setFile] = useState(null); const [preview, setPreview] = useState(null); const [result, setResult] = useState(null); const [loading, setLoading] = useState(false);
  const [scanCooldown, setScanCooldown] = useState(false);
  const onFile = (f) => { if (!f) return; setFile(f); setPreview(URL.createObjectURL(f)); setResult(null); };
  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) onFile(f); };
  const onPaste = (e) => { for (const it of (e.clipboardData?.items || [])) if (it.type.startsWith("image/")) { const f = it.getAsFile(); onFile(f); break; } };
  const pasteFromClipboard = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const it of items) for (const tp of it.types) if (tp.startsWith("image/")) { const blob = await it.getType(tp); onFile(new File([blob], "pasted.png", { type: tp })); return; }
      toast.error("No image in clipboard");
    } catch (e) { toast.error("Clipboard access denied — use Ctrl+V"); }
  };
  useEffect(() => { window.addEventListener("paste", onPaste); return () => window.removeEventListener("paste", onPaste); }, []);
  const analyze = async () => {
    if (!file || scanCooldown) return;
    setScanCooldown(true);
    setTimeout(() => setScanCooldown(false), 5000);
    setLoading(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/scanner/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } }); setResult(r.data); playSound("ding"); }
    catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); playSound("error"); } finally { setLoading(false); }
  };
  const renderSide = (label, items, total) => (
    <div className="card-cartoon p-4">
      <div className="flex justify-between items-center mb-2"><h4 className="font-display text-xl">{label}</h4><Badge className="bg-[#ff6fa3] text-white font-display text-base">{total.toFixed(2)}</Badge></div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {items.map((x, i) => (
          <div key={i} className="relative">
            {x.item ? <ItemCard item={x.item} variant={x.variant} compact testId={`detected-${label}-${i}`}/> : (
              <div className="card-cartoon p-2 text-center text-xs bg-yellow-50 border-2 border-yellow-300">
                <div className="text-yellow-700 font-bold">Unmatched</div>
                <div className="truncate">{x.detected_name}</div>
              </div>
            )}
            {x.potion_status && x.potion_status !== "none" && <span className="absolute -top-2 -right-2 bg-[#3aa8ff] text-white text-[9px] uppercase rounded-full px-1.5 py-0.5">{x.potion_status}</span>}
            {x.confidence < 0.6 && <span className="absolute -bottom-2 left-1 bg-yellow-400 text-yellow-900 text-[9px] uppercase rounded-full px-1.5 py-0.5">?</span>}
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-gray-500 col-span-full text-center py-4">No items detected</div>}
      </div>
    </div>
  );
  return (
    <div className="space-y-4 pop-in">
      <div className="bg-amber-50 border-2 border-amber-400 rounded-2xl p-4 text-center shadow-cartoon-sm">
        <p className="text-amber-800 font-display text-lg"><ShieldAlert className="w-5 h-5 inline-block mr-1.5 -mt-0.5" />AI Scanner is still being improved</p>
        <p className="text-amber-700 text-sm mt-1">The scanner may not always identify pets correctly. For accurate trades, we recommend using the <Link to="/calculator" className="text-[#7649e6] underline font-bold">Manual Calculator</Link> instead. We're actively working on making the AI scanner more reliable!</p>
      </div>
      <AdSlot placement="scanner_top"/>
      <div onDrop={onDrop} onDragOver={(e) => e.preventDefault()} className="card-cartoon p-8 text-center border-4 border-dashed border-[#ff6fa3]/40">
        {preview ? <img src={preview} alt="preview" className="max-h-72 mx-auto rounded-2xl shadow-cartoon"/> : <div><Upload className="w-12 h-12 mx-auto text-[#7649e6]"/><p className="mt-2 font-display text-xl">Drag & drop, paste (Ctrl+V), or upload</p><p className="text-xs text-[#7649e6] mt-1">PNG, JPG, WebP — max 6MB. Use a clear, in-game trade window screenshot for best accuracy.</p></div>}
        <div className="mt-4 flex gap-2 justify-center flex-wrap">
          <label className="cursor-pointer"><input data-testid="scanner-file-input" type="file" accept="image/*" onChange={(e) => onFile(e.target.files[0])} className="hidden"/><span className="inline-flex items-center rounded-2xl bg-[#7649e6] text-white px-4 py-2 shadow-cartoon-sm btn-pop"><Upload className="w-4 h-4 mr-2"/>Choose file</span></label>
          <Button data-testid="paste-button" type="button" onClick={pasteFromClipboard} variant="outline" className="rounded-2xl btn-pop"><Clipboard className="w-4 h-4 mr-2"/>Paste</Button>
          {file && <Button data-testid="analyze-button" onClick={analyze} disabled={loading || scanCooldown} className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a] shadow-cartoon btn-pop">{loading ? "Analyzing…" : scanCooldown ? "Wait..." : "Analyze with AI"}</Button>}
          {file && <Button variant="ghost" onClick={() => { if (preview) URL.revokeObjectURL(preview); setFile(null); setPreview(null); setResult(null); }} className="rounded-2xl">Clear</Button>}
        </div>
      </div>
      {result && (
        <div data-testid="scan-result" className="card-cartoon p-6 pop-in">
          <div className="font-display text-4xl text-center" style={{color: result.diff >= 0 ? "#36a86b" : "#ff5a8a"}}>{result.verdict}</div>
          <div className="text-center mt-1">{result.recommendation}</div>
          {result.scam_signals?.length > 0 && <div className="mt-3 card-cartoon p-3 bg-red-50 border-2 border-red-300"><div className="font-bold text-red-600 flex items-center gap-2"><ShieldAlert className="w-4 h-4"/>Scam alerts</div><ul className="list-disc pl-5 text-sm text-red-700">{result.scam_signals.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {renderSide("Your Side", result.your_side || [], result.your_total || 0)}
            {renderSide("Their Side", result.their_side || [], result.their_total || 0)}
          </div>
        </div>
      )}
    </div>
  );
}

// =================================================================================
// Report Dialog
// =================================================================================
function ReportDialog({ target, tradeId, trigger }) {
  const [open, setOpen] = useState(false); const [reason, setReason] = useState("");
  const submit = async () => {
    try { await api.post("/reports/scam", { target_username: target, reason, trade_id: tradeId }); toast.success("Report submitted to moderators"); playSound("success"); setOpen(false); setReason(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-3xl bg-white border-4 border-red-300">
        <DialogHeader><DialogTitle className="font-display text-2xl text-red-600 flex items-center gap-2"><Flag/>Report {target}</DialogTitle></DialogHeader>
        <Textarea data-testid="report-reason" value={reason} onChange={e=>setReason(e.target.value)} placeholder="Describe the suspicious behavior (scam attempt, phishing, harassment…)" className="rounded-2xl"/>
        <DialogFooter><Button data-testid="submit-report" onClick={submit} disabled={!reason.trim()} className="rounded-2xl bg-red-500 hover:bg-red-600">Submit Report</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =================================================================================
// Review Popup
// =================================================================================
const QUICK_TAGS = [
  { value: "trusted_trader", label: "Trusted Trader" },
  { value: "fast_response", label: "Fast Response" },
  { value: "fair_trade", label: "Fair Trade" },
  { value: "friendly", label: "Friendly" },
  { value: "good_communication", label: "Good Communication" },
];
function StarRating({ value, onChange }) {
  return <div className="flex gap-1 justify-center">
    {[1,2,3,4,5].map(s => (
      <button key={s} type="button" onClick={() => onChange(s)} className={`w-10 h-10 rounded-full flex items-center justify-center transition-all btn-pop ${s <= value ? "text-[#ffb13b] scale-110" : "text-gray-300 hover:text-[#ffb13b]/50"}`}>
        <Star className={`w-7 h-7 ${s <= value ? "fill-[#ffb13b]" : ""}`}/>
      </button>
    ))}
  </div>;
}
function ReviewPopup({ tradeId, targetUsername, open, onOpenChange }) {
  const user = getUser();
  const [rating, setRating] = useState(0);
  const [tags, setTags] = useState([]);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const toggleTag = (v) => setTags(t => t.includes(v) ? t.filter(x => x !== v) : [...t, v]);
  const submit = async () => {
    if (rating === 0) return;
    setSubmitting(true);
    try { await api.post(`/trades/${tradeId}/review`, { rating, tags, text }); playSound("success"); setSubmitted(true); toast.success("Review submitted! Thanks for helping the community."); } catch (e) { toast.error(e.response?.data?.detail || "Error submitting review"); playSound("error"); } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl bg-white border-4 border-[#ffb13b]/40 max-w-md">
        {!submitted ? (<>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-center text-[#2a1f3d]">Rate Your Trading Experience</DialogTitle>
            <p className="text-center text-sm text-[#7649e6]">Trading with <span className="font-bold">{targetUsername || "the other user"}</span></p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-center">
              <p className="text-sm font-semibold mb-2">Your Rating</p>
              <StarRating value={rating} onChange={setRating}/>
            </div>
            <div>
              <p className="text-sm font-semibold mb-2 text-center">Quick Tags</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {QUICK_TAGS.map(t => (
                  <button key={t.value} type="button" onClick={() => toggleTag(t.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all btn-pop ${tags.includes(t.value) ? "bg-[#ff6fa3] text-white shadow-cartoon-sm" : "bg-[#fff1a8]/40 text-[#2a1f3d] border-2 border-[#2a1f3d]/20 hover:border-[#ff6fa3]/40"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold mb-1">Review <span className="text-gray-400">(optional)</span></p>
              <Textarea value={text} onChange={e => setText(e.target.value)} placeholder="Share your experience..." className="rounded-2xl" rows={3} maxLength={500}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-2xl">Skip</Button>
            <Button onClick={submit} disabled={rating === 0 || submitting} className="rounded-2xl bg-[#ffb13b] hover:bg-[#f59f1f] shadow-cartoon btn-pop">{submitting ? "Submitting..." : "Submit Review"}</Button>
          </DialogFooter>
        </>) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-[#36a86b]/20 flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-10 h-10 text-[#36a86b]"/></div>
            <h3 className="font-display text-2xl text-[#36a86b]">Review Submitted!</h3>
            <p className="text-sm text-[#7649e6] mt-2">Your review helps the community make safer trades.</p>
            <Button onClick={() => onOpenChange(false)} className="mt-4 rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a]">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// =================================================================================
// Trade Center
// =================================================================================
function TradesList() {
  const [trades, setTrades] = useState([]); const [q, setQ] = useState(""); const [creating, setCreating] = useState(false);
  const [sort, setSort] = useState("recent"); const [tab, setTab] = useState("all");
  const [filterType, setFilterType] = useState(""); const [filterRarity, setFilterRarity] = useState("");
  const [guestPrompt, setGuestPrompt] = useState(false);
  const [loading, setLoading] = useState(true);
  const { isOnCooldown, trigger } = useCooldown(1500);
  const nav = useNavigate();
  const user = getUser();
  const load = () => {
    const params = { search: q, limit: 30 };
    if (tab === "mine") { params.mine = true; }
    else if (["open","pending_completion","completed","cancelled"].includes(tab)) { params.status = tab; }
    api.get("/trades", { params }).then(r => { setTrades(r.data.trades); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { setLoading(true); const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q, tab]);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);
  useEffect(() => { const h = () => load(); window.addEventListener("ws:trade_update", h); return () => window.removeEventListener("ws:trade_update", h); }, []);

  const filtered = trades.filter(t => {
    const items = [...(t.offering_items || []), ...(t.looking_items || [])];
    if (filterType) {
      if (!items.some(i => i?.type === filterType)) return false;
    }
    if (filterRarity) {
      if (!items.some(i => i?.rarity?.toLowerCase() === filterRarity.toLowerCase())) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((x, y) => {
    if (sort === "value-high") {
      const vx = (x.offering_items || []).reduce((s, i) => s + (i.value || 0), 0);
      const vy = (y.offering_items || []).reduce((s, i) => s + (i.value || 0), 0);
      return vy - vx;
    }
    return new Date(y.created_at) - new Date(x.created_at);
  });

  const deleteTrade = async (id) => {
    if (!window.confirm("Delete this trade?")) return;
    try { await api.delete(`/trades/${id}`); toast.success("Trade deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const timeLeft = (t) => {
    if (!t.expires_at) return null;
    const ms = new Date(t.expires_at) - new Date();
    if (ms < 0) return "Expired";
    const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000);
    return h > 24 ? `${Math.floor(h/24)}d ${h%24}h left` : `${h}h ${m}m left`;
  };

  return (
    <div className="space-y-4">
      <AdSlot placement="trades_top"/>
      <div className="card-cartoon p-4 sm:p-5 bg-gradient-to-br from-[#ffd1e8]/40 to-[#c8efff]/40">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="w-full sm:w-auto">
            <h1 className="font-display text-3xl sm:text-4xl text-[#ff5a8a]">Trade Center</h1>
            <p className="text-xs sm:text-sm">Trades auto-expire after 12h. Max 5 trades/day.</p>
          </div>
          {user ? (
            <Button data-testid="new-trade-button" onClick={() => { if (isOnCooldown("createTrade")) return; trigger("createTrade"); setCreating(true); }} className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a] shadow-cartoon btn-pop w-full sm:w-auto text-sm"><Plus className="w-4 h-4 mr-1"/>Post a Trade</Button>
          ) : (
            <button onClick={() => setGuestPrompt(true)} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#ff6fa3]/80 to-[#7649e6]/80 text-white/90 px-4 py-2 shadow-cartoon-sm btn-pop text-sm font-medium border-2 border-dashed border-white/40 hover:from-[#ff6fa3] hover:to-[#7649e6] hover:text-white w-full sm:w-auto"><Lock className="w-4 h-4"/>Login to Post</button>
          )}
        </div>
        <div className="mt-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <Tabs value={tab} onValueChange={setTab}><TabsList className="rounded-2xl bg-white flex-wrap overflow-x-auto"><TabsTrigger value="all" data-testid="tab-all-trades">All</TabsTrigger><TabsTrigger value="open" data-testid="tab-open">Open</TabsTrigger><TabsTrigger value="pending_completion" data-testid="tab-pending">Pending</TabsTrigger><TabsTrigger value="completed" data-testid="tab-completed">Done</TabsTrigger>{user && <TabsTrigger value="mine" data-testid="tab-my-trades">Mine</TabsTrigger>}</TabsList></Tabs>
          <div className="relative flex-1 min-w-0"><Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"/><Input data-testid="trades-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="rounded-2xl pl-9 bg-white"/></div>
          <div className="flex gap-2 flex-wrap">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className="rounded-2xl w-28 sm:w-36 bg-white text-xs sm:text-sm"><Filter className="w-3 h-3 sm:w-4 sm:h-4 mr-1"/><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="recent">Recent</SelectItem><SelectItem value="value-high">Value</SelectItem></SelectContent>
          </Select>
          <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-type" className="rounded-2xl w-28 sm:w-32 bg-white text-xs"><SelectValue placeholder="Type"/></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem><SelectItem value="pets">Pets</SelectItem><SelectItem value="pet wear">Pet Wear</SelectItem><SelectItem value="vehicles">Vehicles</SelectItem><SelectItem value="strollers">Strollers</SelectItem><SelectItem value="food">Food</SelectItem><SelectItem value="toys">Toys</SelectItem><SelectItem value="eggs">Eggs</SelectItem><SelectItem value="potions">Potions</SelectItem></SelectContent>
          </Select>
          <Select value={filterRarity || "all"} onValueChange={(v) => setFilterRarity(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-rarity" className="rounded-2xl w-28 sm:w-32 bg-white text-xs"><SelectValue placeholder="Rarity"/></SelectTrigger>
            <SelectContent><SelectItem value="all">Any rarity</SelectItem><SelectItem value="common">Common</SelectItem><SelectItem value="uncommon">Uncommon</SelectItem><SelectItem value="rare">Rare</SelectItem><SelectItem value="ultra-rare">Ultra-Rare</SelectItem><SelectItem value="legendary">Legendary</SelectItem></SelectContent>
          </Select>
          </div>
        </div>
      </div>
      {creating && <Protected><CreateTrade onClose={() => { setCreating(false); load(); }}/></Protected>}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {[1,2,3,4,5,6].map(i => <div key={i} className="card-cartoon p-4 animate-pulse"><div className="flex items-center gap-2 mb-3"><div className="w-10 h-10 rounded-full bg-[#ffd1e8]/60"/><div className="flex-1"><div className="h-3 bg-[#ffd1e8]/60 rounded w-24 mb-1"/><div className="h-2 bg-[#ffd1e8]/40 rounded w-16"/></div></div><div className="h-3 bg-[#ffd1e8]/40 rounded w-32 mb-3"/><div className="flex gap-1 mb-2">{[1,2,3].map(j => <div key={j} className="w-10 h-10 rounded-xl bg-[#ffd1e8]/40"/>)}</div><div className="h-2 bg-[#ffd1e8]/30 rounded w-20"/></div>)}
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
        {sorted.map(t => (
          <div key={t.id} data-testid={`trade-card-${t.id}`} className="card-cartoon p-3 sm:p-4 hover:shadow-cartoon-lg transition-shadow group relative">
            <div className="flex items-center gap-2 mb-2">
              {t.owner_avatar && <img src={t.owner_avatar} className="w-10 h-10 rounded-full border-2 border-[#ff6fa3]/30"/>}
              <div className="flex-1 min-w-0">
                <Link to={`/profile/${t.owner_username_lc || t.owner_username}`} className="text-sm font-bold hover:underline truncate block">{t.owner_username}</Link>
                <div className="flex gap-1 flex-wrap">
                  {t.owner_role !== "user" && <Badge className="bg-[#ffb13b] text-white capitalize text-[10px]">{t.owner_role}</Badge>}
                  {t.scam_risk === "high" && <Badge className="bg-red-500 text-white text-[10px]">High Risk</Badge>}
                </div>
              </div>
              {user && user.id === t.owner_id ? (
                <button data-testid={`delete-${t.id}`} onClick={() => deleteTrade(t.id)} className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 border border-red-200 btn-pop" title="Delete"><Trash2 className="w-3.5 h-3.5 text-red-600"/></button>
              ) : user && (
                <ReportDialog target={t.owner_username} tradeId={t.id} trigger={<button data-testid={`report-${t.id}`} className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 border border-red-200 btn-pop" title="Report player"><Flag className="w-3.5 h-3.5 text-red-600"/></button>}/>
              )}
            </div>
            <Link to={`/trades/${t.id}`} className="block">
              <div className="flex items-center gap-2"><div className="font-display text-lg truncate">{t.title}</div>
              {t.status === "completed" && <Badge className="bg-[#36a86b] text-white shrink-0"><CheckCircle className="w-3 h-3 mr-0.5"/>Done</Badge>}
              {t.status === "pending_completion" && <Badge className="bg-[#ffb13b] text-white shrink-0"><Clock className="w-3 h-3 mr-0.5"/>Pending</Badge>}
              {t.status === "cancelled" && <Badge className="bg-gray-500 text-white shrink-0"><X className="w-3 h-3 mr-0.5"/>Cancelled</Badge>}
            </div>
              <div className="text-[10px] uppercase text-[#7649e6] font-bold mt-2">Offering</div>
              <div className="flex gap-1.5 flex-wrap min-h-16">{(t.offering_items || []).slice(0,6).map((i, k) => <ItemCard key={k} item={i} compact/>)}</div>
              <div className="text-[10px] uppercase text-[#7649e6] font-bold mt-2">Looking for</div>
              <div className="flex gap-1.5 flex-wrap min-h-16">{(t.looking_items || []).slice(0,6).map((i, k) => <ItemCard key={k} item={i} compact/>)}</div>
                <div className="mt-2 text-xs flex justify-between text-[#7649e6]">
                  <span>{timeLeft(t) || new Date(t.created_at).toLocaleDateString()}</span>
                  <span className="flex items-center gap-2">
                    {user && user.id === t.owner_id && t.offer_count > 0 && <Badge className="bg-[#ff6fa3] text-white text-[10px]">{t.offer_count} offer{t.offer_count > 1 ? "s" : ""}</Badge>}
                    <span className="font-bold">View Trade →</span>
                  </span>
                </div>
              </Link>
              {user && user.id !== t.owner_id && (
                <button onClick={(e) => { e.preventDefault(); nav(`/messages?trade=${t.id}&with=${t.owner_id}`); }}
                  className="mt-2 w-full py-1.5 rounded-xl bg-[#ff6fa3]/10 hover:bg-[#ff6fa3]/20 text-[#ff5a8a] text-xs font-semibold transition-all btn-pop flex items-center justify-center gap-1">
                  <Mail className="w-3 h-3"/>Message {t.owner_username}
                </button>
              )}
          </div>
        ))}
        {sorted.length === 0 && <div className="col-span-full text-center text-gray-500 py-12 card-cartoon">No trades yet. Be the first!</div>}
      </div>
      )}
      <AuthPrompt
        open={guestPrompt}
        onOpenChange={setGuestPrompt}
        title="Sign in to post trades"
        description="Sign in with Roblox to post trades, message players, and build your reputation safely."
      />
    </div>
  );
}

function CreateTrade({ onClose }) {
  const [title, setTitle] = useState(""); const [desc, setDesc] = useState(""); const [off, setOff] = useState([]); const [look, setLook] = useState([]);
  const [active, setActive] = useState("off"); const [pickerOpen, setPickerOpen] = useState(false);
  const [subCooldown, setSubCooldown] = useState(false);
  const submit = async () => {
    if (subCooldown) return;
    setSubCooldown(true);
    setTimeout(() => setSubCooldown(false), 3000);
    try {
      await api.post("/trades", {
        title, description: desc,
        offering: off.map(i => ({ id: i.id, variant: i.variant || "normal", potion: i.potion || (i.type === "pets" ? "flyride" : "none") })),
        looking_for: look.map(i => ({ id: i.id, variant: i.variant || "normal", potion: i.potion || (i.type === "pets" ? "flyride" : "none") })),
      });
      toast.success("Trade posted!"); playSound("success"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); playSound("error"); }
  };
  return (
    <div className="card-cartoon p-5 pop-in">
      <div className="flex justify-between items-center mb-3"><h3 className="font-display text-2xl">Create Trade</h3><button onClick={onClose}><X/></button></div>
      <Input data-testid="trade-title-input" value={title} onChange={e=>setTitle(e.target.value)} placeholder="Trade title…" className="rounded-2xl mb-2"/>
      <Textarea data-testid="trade-desc-input" value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Description (no external links — scam shield will block them!)" className="rounded-2xl mb-3"/>
      <Tabs value={active} onValueChange={setActive}><TabsList className="grid grid-cols-2 rounded-2xl bg-[#fff1a8]/40"><TabsTrigger value="off" data-testid="tab-offering">Offering ({off.length})</TabsTrigger><TabsTrigger value="look" data-testid="tab-looking">Looking for ({look.length})</TabsTrigger></TabsList>
        <TabsContent value="off">
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {off.map((i, idx) => <div key={idx} className="relative"><ItemCard item={i} variant={i.variant || "normal"} compact/><button onClick={() => setOff(off.filter((_,k)=>k!==idx))} className="absolute -top-2 -left-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"><X className="w-3 h-3"/></button></div>)}
            <button data-testid="add-offering" onClick={() => { setActive("off"); setPickerOpen(true); }} className="aspect-square rounded-2xl border-2 border-dashed border-[#ff6fa3]/40 bg-[#ffd1e8]/30 hover:bg-[#ffd1e8]/60 btn-pop flex items-center justify-center"><Plus className="w-6 h-6 text-[#ff6fa3]"/></button>
          </div>
        </TabsContent>
        <TabsContent value="look">
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {look.map((i, idx) => <div key={idx} className="relative"><ItemCard item={i} variant={i.variant || "normal"} compact/><button onClick={() => setLook(look.filter((_,k)=>k!==idx))} className="absolute -top-2 -left-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"><X className="w-3 h-3"/></button></div>)}
            <button data-testid="add-looking" onClick={() => { setActive("look"); setPickerOpen(true); }} className="aspect-square rounded-2xl border-2 border-dashed border-[#ff6fa3]/40 bg-[#ffd1e8]/30 hover:bg-[#ffd1e8]/60 btn-pop flex items-center justify-center"><Plus className="w-6 h-6 text-[#ff6fa3]"/></button>
          </div>
        </TabsContent>
      </Tabs>
      <Button data-testid="submit-trade" onClick={submit} disabled={!title || subCooldown} className="rounded-2xl w-full mt-3 bg-[#ff6fa3] hover:bg-[#ff5a8a] shadow-cartoon btn-pop">{subCooldown ? "Please wait..." : "Post Trade"}</Button>
      <ItemPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} onPick={(i) => active === "off" ? setOff([...off, i]) : setLook([...look, i])}/>
    </div>
  );
}

function TradeDetail() {
  const { id } = useParams(); const [trade, setTrade] = useState(null);
  const [convs, setConvs] = useState([]);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);
  const user = getUser();
  const isOwner = user && trade?.owner_id === user.id;
  const nav = useNavigate();

  const loadTrade = async () => {
    let r;
    try { r = await api.get(`/trades/${id}`); setTrade(r.data); } catch { return; }
    if (!user) return;
    if (user.id === r.data.owner_id) {
      try {
        await api.post(`/trades/${id}/join`);
        const cr = await api.get(`/trades/${id}/conversations`);
        setConvs(cr.data.conversations);
      } catch {}
    } else {
      try { await api.post(`/trades/${id}/join`); } catch {}
    }
  };
  useEffect(() => { loadTrade(); }, [id]);

  const handleComplete = async (targetUserId) => {
    try {
      const r = await api.post(`/trades/${id}/complete`, { target_user_id: targetUserId });
      if (r.data.status === "completed") {
        setTrade(t => ({...t, status: "completed"}));
        toast.success("Trade completed!");
        playSound("success");
      } else {
        setTrade(t => ({...t, status: "pending_completion", completion_request: r.data.completion_request}));
        toast.success("Completion request sent! Waiting for them to confirm.");
        playSound("pop");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleCompleteClick = () => {
    const targets = isOwner ? (convs || []).map(c => ({ id: c.user_id, username: c.username, avatar: c.avatar }))
      : [{ id: trade.owner_id, username: trade.owner_username, avatar: trade.owner_avatar }].filter(t => t.id !== user?.id);
    if (targets.length === 1) {
      handleComplete(targets[0].id);
    } else if (targets.length > 1) {
      setCompleteTarget(targets);
      setCompleteOpen(true);
    } else {
      toast.error("No other participants to complete with.");
    }
  };

  const handleConfirmComplete = async () => {
    try {
      const r = await api.post(`/trades/${id}/confirm-complete`, {});
      setTrade(t => ({...t, status: "completed"}));
      toast.success("Trade completed!");
      playSound("success");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleCancelCompletion = async () => {
    if (!window.confirm("Cancel your completion request?")) return;
    try {
      const r = await api.post(`/trades/${id}/cancel-completion`);
      setTrade(t => ({...t, status: "open", completion_request: undefined}));
      toast.success("Completion request cancelled.");
      playSound("pop");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleCancelTrade = async () => {
    if (!window.confirm("Cancel this trade? This cannot be undone.")) return;
    try {
      await api.patch(`/trades/${id}/cancel`);
      setTrade(t => ({...t, status: "cancelled"}));
      toast.success("Trade cancelled.");
      playSound("pop");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editOff, setEditOff] = useState([]);
  const [editLook, setEditLook] = useState([]);
  const [editPickerOpen, setEditPickerOpen] = useState(false);
  const [editPickerFor, setEditPickerFor] = useState("off");

  const startEdit = () => {
    setEditTitle(trade.title);
    setEditDesc(trade.description || "");
    setEditOff((trade.offering_items || []).filter(Boolean));
    setEditLook((trade.looking_items || []).filter(Boolean));
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      const r = await       api.put(`/trades/${id}`, {
        title: editTitle,
        description: editDesc,
        offering: editOff.map(i => ({ id: i.id, variant: i.selected_variant || i.variant || "normal", potion: i.selected_potion || i.potion || (i.type === "pets" ? "flyride" : "none") })),
        looking_for: editLook.map(i => ({ id: i.id, variant: i.selected_variant || i.variant || "normal", potion: i.selected_potion || i.potion || (i.type === "pets" ? "flyride" : "none") })),
      });
      setTrade(r.data);
      setEditing(false);
      toast.success("Trade updated!");
      playSound("success");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const isParticipant = user && trade?.participants?.includes(user.id);
  const isCompleted = trade?.status === "completed";
  if (!trade) return <div className="text-center py-20 font-display text-2xl text-[#7649e6] animate-pulse">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Trade info */}
      <div className="card-cartoon p-5">
        <div className="flex items-center gap-2 mb-2">
          {trade.owner_avatar && <img src={trade.owner_avatar} className="w-12 h-12 rounded-full border-2 border-[#ff6fa3]/30"/>}
          <div className="flex-1"><Link to={`/profile/${trade.owner_username_lc || trade.owner_username}`} className="font-bold hover:underline">{trade.owner_username}</Link><div className="text-xs">Rep {trade.owner_reputation || 0}</div></div>
          <div className="flex items-center gap-1">
            {user && user.id !== trade.owner_id && (
              <button onClick={() => nav(`/messages?trade=${trade.id}&with=${trade.owner_id}`)} className="p-2 rounded-full bg-[#ff6fa3]/10 hover:bg-[#ff6fa3]/20 btn-pop" title="Message">
                <Mail className="w-4 h-4 text-[#ff5a8a]"/>
              </button>
            )}
            {user && user.id !== trade.owner_id &&
              <ReportDialog target={trade.owner_username} tradeId={trade.id} trigger={<Button data-testid="report-player" variant="outline" className="rounded-2xl border-red-300 text-red-600 hover:bg-red-50"><Flag className="w-4 h-4 mr-1"/>Report</Button>}/>
            }
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="font-display text-3xl">{trade.title}</h2>
          {trade.status === "completed" && <Badge className="bg-[#36a86b] text-white"><CheckCircle className="w-3 h-3 mr-1"/>Completed</Badge>}
          {trade.status === "pending_completion" && <Badge className="bg-[#ffb13b] text-white"><Clock className="w-3 h-3 mr-1"/>Pending</Badge>}
          {trade.status === "cancelled" && <Badge className="bg-gray-500 text-white"><X className="w-3 h-3 mr-1"/>Cancelled</Badge>}
        </div>
        <p className="text-sm mt-1">{trade.description}</p>
        <div className="mt-4"><div className="text-sm text-[#7649e6] mb-1">Offering</div><div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">{(trade.offering_items || []).map((i, k) => <ItemCard key={k} item={i}/>)}</div></div>
        <div className="mt-4"><div className="text-sm text-[#7649e6] mb-1">Looking for</div><div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">{(trade.looking_items || []).map((i, k) => <ItemCard key={k} item={i}/>)}</div></div>
        {isOwner && convs.length > 1 && (
          <div className="mt-4 pt-3 border-t-2 border-[#2a1f3d]/10">
            <div className="text-xs font-semibold text-[#7649e6] mb-2 uppercase">Offers ({convs.length})</div>
            <div className="flex flex-wrap gap-2">
              {convs.map(c => (
                <button key={c.user_id} onClick={() => nav(`/messages?trade=${trade.id}&with=${c.user_id}`)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all btn-pop bg-[#fff1a8]/40 hover:bg-[#ffd1e8]/60`}>
                  <MessageSquare className="w-3 h-3"/>
                  {c.avatar && <img src={c.avatar} className="w-4 h-4 rounded-full"/>}
                  {c.username}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Editing mode */}
        {editing && isOwner && (
          <div className="mt-4 pt-3 border-t-2 border-[#2a1f3d]/10 space-y-2 pop-in">
            <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder="Trade title" className="rounded-2xl"/>
            <Textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description" className="rounded-2xl"/>
              <div className="flex gap-2">
              <div className="flex-1">
                <div className="text-xs font-bold text-[#36a86b] mb-1">Offering ({editOff.length})</div>
                <div className="flex gap-1 flex-wrap">
                  {editOff.map((i, k) => <div key={k} className="relative"><ItemCard item={i} compact/><button onClick={() => setEditOff(editOff.filter((_,x)=>x!==k))} className="absolute -top-2 -left-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"><X className="w-3 h-3"/></button></div>)}
                  <button onClick={() => { setEditPickerFor("off"); setEditPickerOpen(true); }} className="w-10 h-10 rounded-xl border-2 border-dashed border-[#36a86b]/40 flex items-center justify-center"><Plus className="w-4 h-4 text-[#36a86b]"/></button>
                </div>
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold text-[#7649e6] mb-1">Looking ({editLook.length})</div>
                <div className="flex gap-1 flex-wrap">
                  {editLook.map((i, k) => <div key={k} className="relative"><ItemCard item={i} compact/><button onClick={() => setEditLook(editLook.filter((_,x)=>x!==k))} className="absolute -top-2 -left-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"><X className="w-3 h-3"/></button></div>)}
                  <button onClick={() => { setEditPickerFor("look"); setEditPickerOpen(true); }} className="w-10 h-10 rounded-xl border-2 border-dashed border-[#7649e6]/40 flex items-center justify-center"><Plus className="w-4 h-4 text-[#7649e6]"/></button>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveEdit} className="rounded-2xl bg-[#36a86b] hover:bg-[#2d8f5e] shadow-cartoon btn-pop flex-1">Save</Button>
              <Button onClick={() => setEditing(false)} variant="outline" className="rounded-2xl flex-1">Cancel</Button>
            </div>
            <ItemPickerDialog open={editPickerOpen} onOpenChange={setEditPickerOpen} onPick={(item) => {
              if (editPickerFor === "off") {
                if (!editOff.some(x => x.id === item.id)) setEditOff([...editOff, item]);
              } else {
                if (!editLook.some(x => x.id === item.id)) setEditLook([...editLook, item]);
              }
            }}/>
          </div>
        )}
        {/* Action buttons */}
            {!editing && (
          <div className="mt-4 pt-3 border-t-2 border-[#2a1f3d]/10 space-y-2">
            {/* Owner actions */}
            {isOwner && trade.status === "open" && (
              <div className="flex gap-2 flex-wrap">
                <Button onClick={startEdit} variant="outline" className="rounded-2xl flex-1"><Pencil className="w-4 h-4 mr-1"/>Edit</Button>
                <Button onClick={handleCompleteClick} className="flex-1 rounded-2xl bg-[#36a86b] hover:bg-[#2d8f5e] shadow-cartoon btn-pop"><CheckCircle className="w-4 h-4 mr-2"/>Mark Trade as Completed</Button>
                <Button onClick={handleCancelTrade} variant="outline" className="rounded-2xl flex-1 border-red-300 text-red-600 hover:bg-red-50"><X className="w-4 h-4 mr-1"/>Cancel Trade</Button>
              </div>
            )}
            {isOwner && trade.status === "pending_completion" && (
              <div className="flex gap-2">
                {(trade.completion_request?.target_id === user?.id) ? (
                  <Button onClick={handleConfirmComplete} className="flex-1 rounded-2xl bg-[#36a86b] hover:bg-[#2d8f5e] shadow-cartoon btn-pop"><CheckCircle className="w-4 h-4 mr-2"/>Confirm Completion</Button>
                ) : (
                  <Button onClick={handleCancelCompletion} variant="outline" className="flex-1 rounded-2xl border-amber-300 text-amber-700 hover:bg-amber-50"><Clock className="w-4 h-4 mr-2"/>Cancel My Request</Button>
                )}
                <Button onClick={handleCancelTrade} variant="outline" className="rounded-2xl border-red-300 text-red-600 hover:bg-red-50"><X className="w-4 h-4 mr-1"/>Cancel Trade</Button>
              </div>
            )}
            {isOwner && trade.status === "cancelled" && (
              <Button onClick={() => { if (window.confirm("Delete this trade permanently?")) api.delete(`/trades/${id}`).then(() => { toast.success("Deleted"); window.location.href = "/trades"; }); }} variant="outline" className="w-full rounded-2xl border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-2"/>Delete Trade</Button>
            )}
            {/* Non-owner participant actions */}
            {isParticipant && !isOwner && trade.status === "pending_completion" && trade.completion_request?.target_id === user?.id && (
              <Button onClick={handleConfirmComplete} className="w-full rounded-2xl bg-[#36a86b] hover:bg-[#2d8f5e] shadow-cartoon btn-pop"><CheckCircle className="w-4 h-4 mr-2"/>Confirm Completion</Button>
            )}
            {isParticipant && !isOwner && trade.status === "pending_completion" && trade.completion_request?.requester_id === user?.id && (
              <Button onClick={handleCancelCompletion} variant="outline" className="w-full rounded-2xl border-amber-300 text-amber-700 hover:bg-amber-50"><Clock className="w-4 h-4 mr-2"/>Cancel My Request</Button>
            )}
          </div>
        )}
      </div>
      {/* Player selection dialog for completion */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="rounded-3xl bg-white border-4 border-[#36a86b]/30 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#2a1f3d] flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#36a86b]"/> Select Player
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 mb-3">Who did you complete this trade with?</p>
          <div className="space-y-2">
            {(completeTarget || []).map(t => (
              <button key={t.id} onClick={() => { setCompleteOpen(false); handleComplete(t.id); }}
                className="w-full flex items-center gap-3 p-3 rounded-2xl bg-[#fff7e8] hover:bg-[#ffd1e8]/40 transition-all btn-pop border-2 border-transparent hover:border-[#36a86b]/30">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ff6fa3] to-[#c279ff] flex items-center justify-center text-white font-bold text-sm overflow-hidden shrink-0">
                  {t.avatar ? <img src={t.avatar} className="w-full h-full object-cover"/> : t.username?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="text-left flex-1">
                  <div className="font-semibold text-sm">{t.username}</div>
                  <div className="text-[11px] text-[#7649e6]">Select to send completion request</div>
                </div>
                <CheckCircle className="w-5 h-5 text-[#36a86b]/50"/>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =================================================================================
// Messages (standalone inbox)
// =================================================================================
function Messages() {
  const [convs, setConvs] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewSubmit, setReviewSubmit] = useState(false);
  const [reviewedTrades, setReviewedTrades] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const user = getUser();
  const nav = useNavigate();
  const msgCountRef = useRef({});

  const [msgLoading, setMsgLoading] = useState(false);

  const loadConvs = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const preselectTrade = params.get("trade");
      const preselectUser = params.get("with");
      // Auto-join if navigating from offer link so conversation exists
      if (preselectTrade && preselectUser && user) {
        try { await api.post(`/trades/${preselectTrade}/join`); } catch {}
      }
      const r = await api.get("/conversations");
      setConvs(r.data.conversations);
      if (r.data.conversations.length > 0 && !activeConv) {
        const target = preselectTrade && preselectUser
          ? r.data.conversations.find(c => c.trade_id === preselectTrade && c.other_user.id === preselectUser)
          : null;
        selectConv(target || r.data.conversations[0]);
      }
    } catch {}
    setLoading(false);
  };

  const selectConv = async (conv) => {
    if (!conv) return;
    setActiveConv(conv);
    setText("");
    setMsgLoading(true);
    try {
      const r = await api.get(`/trades/${conv.trade_id}/messages`, { params: { with_user: conv.other_user.id } });
      setMsgs(r.data.messages);
      msgCountRef.current[conv.trade_id + "_" + conv.other_user.id] = r.data.messages.length;
    } catch {}
    setMsgLoading(false);
  };

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/trades/${activeConv.trade_id}/messages`, { content: text, with_user_id: activeConv.other_user.id });
      setMsgs(m => [...m, r.data]);
      setText("");
      if (r.data.flagged) { toast.error("Blocked by scam shield."); playSound("error"); } else playSound("pop");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setSending(false);
  };

  const handleSystemConfirm = async (action) => {
    try {
      const r = await api.post(`/trades/${activeConv.trade_id}/confirm-complete`, {});
      if (r.data.status === "completed") {
        setActiveConv(c => ({...c, trade_status: "completed"}));
        playSound("success");
        // Reload messages to show the completed system message
        const ms = await api.get(`/trades/${activeConv.trade_id}/messages`, { params: { with_user: activeConv.other_user.id } });
        setMsgs(ms.data.messages);
        // Refresh conversations
        const rc = await api.get("/conversations");
        setConvs(rc.data.conversations);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const submitReview = async () => {
    if (reviewRating < 1 || reviewSubmit) return;
    setReviewSubmit(true);
    try {
      await api.post(`/trades/${activeConv.trade_id}/review`, { rating: reviewRating, text: reviewText });
      toast.success("Review submitted!");
      setReviewedTrades(r => ({...r, [activeConv.trade_id]: true}));
      setShowReview(false);
      setReviewRating(0);
      setReviewText("");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); if (e.response?.data?.detail?.includes("already reviewed")) setReviewedTrades(r => ({...r, [activeConv.trade_id]: true})); }
    setReviewSubmit(false);
  };

  const toggleStar = async (conv) => {
    try {
      const r = await api.post(`/trades/${conv.trade_id}/chat/${conv.other_user.id}/star`);
      const starred = r.data.starred;
      setConvs(convs.map(c =>
        c.trade_id === conv.trade_id && c.other_user.id === conv.other_user.id ? {...c, starred} : c
      ));
      if (activeConv?.trade_id === conv.trade_id && activeConv?.other_user?.id === conv.other_user.id) {
        setActiveConv(c => ({...c, starred}));
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const toggleArchive = async (conv) => {
    try {
      const r = await api.post(`/trades/${conv.trade_id}/chat/${conv.other_user.id}/archive`);
      const archived = r.data.archived;
      setConvs(convs.map(c =>
        c.trade_id === conv.trade_id && c.other_user.id === conv.other_user.id ? {...c, archived} : c
      ));
      if (activeConv?.trade_id === conv.trade_id && activeConv?.other_user?.id === conv.other_user.id) {
        setActiveConv(c => ({...c, archived}));
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  // Poll messages for active conversation every 3s
  useEffect(() => {
    if (!activeConv) return;
    const key = activeConv.trade_id + "_" + activeConv.other_user.id;
    const poll = async () => {
      try {
        const r = await api.get(`/trades/${activeConv.trade_id}/messages`, { params: { with_user: activeConv.other_user.id } });
        const newMsgs = r.data.messages;
        const prevCount = msgCountRef.current[key] || 0;
        if (newMsgs.length > prevCount) {
          setMsgs(newMsgs);
          const latest = newMsgs[newMsgs.length - 1];
          if (latest.sender_id !== user?.id) {
            playSound("notification");
            if (document.hidden && "Notification" in window && Notification.permission === "granted") {
              new Notification("New message", { body: `${latest.sender_username}: ${latest.content.length > 80 ? latest.content.slice(0, 80) + "..." : latest.content}`, icon: latest.sender_avatar });
            }
          }
          msgCountRef.current[key] = newMsgs.length;
        }
      } catch {}
    };
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [activeConv, user?.id]);

  const deleteConv = async (conv) => {
    try {
      await api.delete(`/trades/${conv.trade_id}/chat/${conv.other_user.id}`);
      setConvs(convs.filter(c => !(c.trade_id === conv.trade_id && c.other_user.id === conv.other_user.id)));
      if (activeConv?.trade_id === conv.trade_id && activeConv?.other_user?.id === conv.other_user.id) {
        setActiveConv(null);
        setMsgs([]);
      }
      toast.success("Conversation deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setDeleteConfirm(null);
  };

  // Refresh conversations list every 15s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const r = await api.get("/conversations");
        setConvs(r.data.conversations);
      } catch {}
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { loadConvs(); }, []);

  // Live message delivery via WebSocket
  useEffect(() => {
    const handler = (ev) => {
      const msg = ev.detail.message;
      if (!activeConv || !msg) return;
      if (msg.trade_id === activeConv.trade_id) {
        setMsgs(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          const updated = [...prev, msg];
          msgCountRef.current[activeConv.trade_id + "_" + activeConv.other_user.id] = updated.length;
          if (msg.sender_id !== user?.id) {
            playSound("notification");
            if (document.hidden && "Notification" in window && Notification.permission === "granted") {
              new Notification("New message", { body: `${msg.sender_username}: ${msg.content.length > 80 ? msg.content.slice(0, 80) + "..." : msg.content}`, icon: msg.sender_avatar });
            }
            loadConvs();
          }
          return updated;
        });
      }
    };
    window.addEventListener("ws:message", handler);
    return () => window.removeEventListener("ws:message", handler);
  }, [activeConv, user?.id]);

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (diff < 172800000) return "Yesterday";
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  if (loading) return <div className="text-center py-20 font-display text-2xl text-[#7649e6] animate-pulse">Loading messages...</div>;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="w-6 h-6 text-[#ff5a8a]"/>
        <h1 className="font-display text-3xl text-[#2a1f3d]">Messages</h1>
        {convs.length > 0 && <span className="text-sm text-gray-500 ml-auto">{convs.length} conversation{convs.length !== 1 ? "s" : ""}</span>}
      </div>
      {convs.length === 0 ? (
        <div className="card-cartoon p-12 text-center">
          <Mail className="w-16 h-16 mx-auto text-gray-300"/>
          <p className="font-display text-2xl mt-3 text-gray-500">No conversations yet</p>
          <p className="text-sm text-gray-400 mt-1">Start by joining a trade or creating one!</p>
          <Button onClick={() => nav("/trades")} className="mt-4 rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a] shadow-cartoon btn-pop"><MessageSquare className="w-4 h-4 mr-2"/>Browse Trades</Button>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row gap-4 min-h-[300px] md:min-h-[500px] md:h-[calc(100vh-220px)]">
          {/* Inbox sidebar */}
          <div className="w-full md:w-72 shrink-0 card-cartoon p-2 overflow-auto scrollbar-thin max-h-[40vh] md:max-h-none">
            {[...convs].sort((a, b) => {
              if (a.starred !== b.starred) return a.starred ? -1 : 1;
              if (a.unread !== b.unread) return a.unread ? -1 : 1;
              if (a.archived !== b.archived) return a.archived ? 1 : -1;
              return 0;
            }).map(c => {
              const isActive = activeConv?.trade_id === c.trade_id && activeConv?.other_user?.id === c.other_user.id;
              return (
                <button key={c.trade_id + "_" + c.other_user.id} onClick={() => selectConv(c)}
                  className={`w-full text-left p-2.5 sm:p-3 rounded-2xl mb-1 transition-all ${isActive ? "bg-[#ff6fa3]/15 shadow-cartoon-sm" : "hover:bg-[#ffd1e8]/40"} ${c.archived && !c.unread ? "opacity-50" : ""}`}>
                  <div className="flex items-center gap-2">
                    <div className="relative shrink-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-[#ff6fa3] to-[#c279ff] flex items-center justify-center text-white font-bold text-xs sm:text-sm overflow-hidden">
                        {c.other_user.avatar ? <img src={c.other_user.avatar} className="w-full h-full object-cover"/> : c.other_user.username?.[0]?.toUpperCase() || "?"}
                      </div>
                      {c.unread && <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full border-2 border-white"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-semibold text-xs sm:text-sm truncate">{c.other_user.username}</span>
                        <span className="text-[10px] text-gray-400 shrink-0">{formatTime(c.last_message_at)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={`text-xs truncate flex-1 ${c.unread ? "font-bold text-[#2a1f3d]" : "text-gray-500"}`}>{c.last_message || "No messages yet"}</span>
                        <div className="hidden sm:flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                          <button onClick={() => toggleStar(c)} className="p-0.5 hover:scale-110 transition-transform" title={c.starred ? "Unstar" : "Star"}>
                            <Star className={`w-3 h-3 ${c.starred ? "fill-[#ffb13b] text-[#ffb13b]" : "text-gray-300 hover:text-gray-400"}`}/>
                          </button>
                          <button onClick={() => toggleArchive(c)} className="p-0.5 hover:scale-110 transition-transform" title={c.archived ? "Unarchive" : "Archive"}>
                            <Archive className={`w-3 h-3 ${c.archived ? "text-[#7649e6]" : "text-gray-300 hover:text-gray-400"}`}/>
                          </button>
                          <button onClick={() => { if (c.trade_status !== "completed") { toast.error("Only completed trades can be deleted"); } else { setDeleteConfirm(c); } }} className="p-0.5 hover:scale-110 transition-transform" title={c.trade_status === "completed" ? "Delete conversation" : "Only completed trades can be deleted"}>
                            <Trash2 className={`w-3 h-3 ${c.trade_status === "completed" ? "text-gray-300 hover:text-red-400" : "text-gray-200 cursor-not-allowed"}`}/>
                          </button>
                        </div>
                      </div>
                      <div className="text-[10px] text-[#7649e6]/60 truncate mt-0.5">{c.trade_title}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Chat panel */}
          {activeConv ? (
            <div className="flex-1 card-cartoon p-3 sm:p-4 flex flex-col min-h-0 max-h-[70vh] md:max-h-none">
              <div className="flex items-center gap-2 pb-3 border-b-2 border-[#2a1f3d]/10 shrink-0">
                <Link to={`/profile/${activeConv.other_user.username}`} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#ff6fa3] to-[#c279ff] flex items-center justify-center text-white font-bold text-xs overflow-hidden hover:ring-2 hover:ring-[#ff6fa3]/50 shrink-0">
                  {activeConv.other_user.avatar ? <img src={activeConv.other_user.avatar} className="w-full h-full object-cover"/> : activeConv.other_user.username?.[0]?.toUpperCase() || "?"}
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/profile/${activeConv.other_user.username}`} className="font-semibold text-sm truncate hover:underline block">{activeConv.other_user.username}</Link>
                  <div className="text-[11px] text-gray-400 truncate">{activeConv.trade_title}</div>
                </div>
                <Badge className="bg-[#36a86b] text-white text-[10px] shrink-0">Shielded</Badge>
              </div>
              <div className="flex-1 overflow-auto scrollbar-thin space-y-2 py-3 pr-1">
                {msgLoading ? <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-[#7649e6]"/></div> : msgs.length === 0 ? <div className="text-center text-gray-400 text-sm py-10">No messages yet. Start a conversation!</div> : msgs.map(m => {
                  if (m.system_message) {
                    const action = m.completion_action || {};
                    return (
                      <div key={m.id} className="p-3 rounded-2xl bg-[#fff7e8] border-2 border-[#ffb13b]/30 max-w-[90%] mx-auto text-center">
                        <div className="text-sm text-[#2a1f3d]">{m.content}</div>
                        {action.type === "confirm_completion" && action.target_id === user?.id && (
                          <Button onClick={() => handleSystemConfirm(action)} className="mt-2 rounded-2xl bg-[#36a86b] hover:bg-[#2d8f5e] shadow-cartoon-sm btn-pop text-sm">
                            <CheckCircle className="w-4 h-4 mr-1"/>Confirm Completion
                          </Button>
                        )}
                        {action.type === "completed" && (
                          <div className="mt-1 text-[11px] text-[#36a86b] font-semibold flex items-center justify-center gap-1">
                            <CheckCircle className="w-3.5 h-3.5"/>Trade Completed
                          </div>
                        )}
                        {action.type === "completed" && !reviewedTrades[activeConv.trade_id] && (
                          <div className="mt-3 border-t-2 border-[#ffb13b]/20 pt-3">
                            <div className="flex items-center justify-center gap-0.5 mb-2">
                              {[1,2,3,4,5].map(s => (
                                <button key={s} onClick={() => setReviewRating(s)} className="p-0.5">
                                  <Star className={`w-5 h-5 ${s <= reviewRating ? "fill-[#ffb13b] text-[#ffb13b]" : "text-gray-300"}`}/>
                                </button>
                              ))}
                            </div>
                            <Input value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Write a review (optional)..." className="rounded-2xl text-sm mb-2" maxLength={500}/>
                            <Button onClick={submitReview} disabled={reviewRating < 1 || reviewSubmit} className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a] shadow-cartoon-sm btn-pop text-sm w-full">
                              {reviewSubmit ? "Submitting..." : "Submit Review"}
                            </Button>
        </div>
      )}
      {/* Delete confirmation dialog - only for completed trades */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="rounded-3xl bg-white border-4 border-red-400/30 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#2a1f3d] flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500"/> Delete Conversation
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete this conversation with <strong>{deleteConfirm?.other_user?.username}</strong>? This cannot be undone.
          </p>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => setDeleteConfirm(null)} className="flex-1 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-200 shadow-cartoon-sm btn-pop">
              No, Keep It
            </Button>
            <Button onClick={() => deleteConfirm && deleteConv(deleteConfirm)} className="flex-1 rounded-2xl bg-red-500 hover:bg-red-600 text-white shadow-cartoon btn-pop">
              <Trash2 className="w-4 h-4 mr-1"/> Yes, Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
                  return (
                    <div key={m.id} className={`p-2.5 rounded-2xl max-w-[85%] ${m.flagged ? "bg-red-100 border-2 border-red-400" : (m.sender_id === user?.id ? "bg-[#fff1a8]/60 ml-auto" : "bg-[#c8efff]/60 mr-auto")}`}>
                      <div className="text-[11px] font-bold flex items-center gap-1 text-[#7649e6]">{m.sender_username}{m.sender_role !== "user" && <Crown className="w-3 h-3 text-[#ffb13b]"/>}</div>
                      <div className="text-sm mt-0.5">{m.content}</div>
                      <div className="text-[10px] text-gray-400 text-right mt-1">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 pt-2 border-t-2 border-[#2a1f3d]/10 shrink-0">
                <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder="Type a message..." className="rounded-2xl" disabled={activeConv.trade_status === "completed"}/>
                <Button onClick={send} disabled={!text.trim() || sending || activeConv.trade_status === "completed"} className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a] shrink-0"><Send className="w-4 h-4"/></Button>
              </div>
              {activeConv.trade_status === "completed" && <div className="text-[11px] text-gray-400 text-center mt-1"><Lock className="w-3 h-3 inline mr-1"/>Chat is closed — trade completed</div>}
            </div>
          ) : (
            <div className="flex-1 card-cartoon p-12 text-center flex items-center justify-center">
              <div>
                <Mail className="w-12 h-12 mx-auto text-gray-300"/>
                <p className="font-display text-lg text-gray-500 mt-3">Select a conversation</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =================================================================================
// Profile + Editor
// =================================================================================
const RANK_CONFIG = {
  "Legendary Trader": { color: "from-[#ff4500] to-[#cc3700]", icon: Crown, min: 250, desc: "Top-tier legend among traders" },
  "Elite Trader": { color: "from-[#7c3aed] to-[#5b21b6]", icon: Award, min: 100, desc: "Elite status with proven excellence" },
  "Verified Trader": { color: "from-[#2563eb] to-[#1d4ed8]", icon: Shield, min: 50, desc: "Verified through consistent trades" },
  "Trusted Trader": { color: "from-[#059669] to-[#047857]", icon: TrendingUp, min: 20, desc: "Building trust in the community" },
  "Active Trader": { color: "from-[#d97706] to-[#b45309]", icon: UserRound, min: 0, desc: "Started trading journey" },
};
function getTradingRank(p) {
  if (!p) return RANK_CONFIG["Active Trader"];
  const rc = p.review_count || 0;
  const completed = p.trades_completed || 0;
  if (rc >= 250) return RANK_CONFIG["Legendary Trader"];
  if (rc >= 100) return RANK_CONFIG["Elite Trader"];
  if (rc >= 50) return RANK_CONFIG["Verified Trader"];
  if (rc >= 20) return RANK_CONFIG["Trusted Trader"];
  if (completed >= 5) return RANK_CONFIG["Active Trader"];
  return { color: "from-[#9ca3af] to-[#6b7280]", icon: Shield, min: 0, desc: "New to trading" };
}
function renderStars(rating, size = "w-4 h-4") {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  const stars = [];
  for (let i = 0; i < 5; i++) {
    if (i < full) stars.push(<Star key={i} className={`${size} fill-[#ffb13b] text-[#ffb13b]`}/>);
    else if (i === full && half) stars.push(<Star key={i} className={`${size} fill-[#ffb13b]/50 text-[#ffb13b]`}/>);
    else stars.push(<Star key={i} className={`${size} text-gray-300`}/>);
  }
  return stars;
}
function Profile() {
  const { username } = useParams();
  const [p, setP] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState("reviews");
  const user = getUser();
  const load = () => api.get(`/profile/${username}`).then(r => { setP(r.data); api.get(`/reviews/${r.data.roblox_username}`, { params: { limit: 50 } }).then(rr => { setReviews(rr.data.reviews); setReviewTotal(rr.data.total); }).catch(() => {}); }).catch(() => setP(false));
  useEffect(() => { load(); }, [username]);
  if (p === null) return <div className="flex items-center justify-center py-20"><div className="font-display text-2xl text-[#7649e6] animate-pulse">Loading profile...</div></div>;
  if (p === false) return <div className="card-cartoon p-12 text-center"><UserRound className="w-16 h-16 mx-auto text-gray-300"/><div className="font-display text-3xl mt-2 text-gray-500">User not found</div></div>;
  const isMe = user && user.id === p.id;
  const rank = getTradingRank(p);
  const RankIcon = rank.icon;
  const invValue = (p.inventory_items || []).reduce((s, i) => s + (i.value || 0), 0);
  const createdDate = p.created_at ? new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Unknown";
  const lastLogin = p.last_login ? new Date(p.last_login).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Unknown";
  const pcolors = p.profile_colors || {};
  const primaryColor = pcolors.primary || "#ff6fa3";
  const accentColor = pcolors.accent || "#c279ff";
  const STATUS_CONFIG = {
    online: { icon: CheckCircle, label: "Online", color: "#22c55e", bg: "bg-[#22c55e]" },
    idle: { icon: Clock, label: "Idle", color: "#eab308", bg: "bg-[#eab308]" },
    dnd: { icon: X, label: "Do Not Disturb", color: "#ef4444", bg: "bg-[#ef4444]" },
  };
  const userStatus = p.status || "online";
  const StatusIcon = STATUS_CONFIG[userStatus]?.icon || CheckCircle;
  const groupItems = (items) => {
    const groups = {};
    (items || []).forEach(i => {
      const key = `${i.id}_${i.variant || "normal"}_${i.potion || "none"}`;
      if (groups[key]) groups[key].quantity += i.quantity || 1;
      else groups[key] = { ...i, quantity: i.quantity || 1 };
    });
    return Object.values(groups);
  };
  const groupedInv = groupItems(p.inventory_items);
  const groupedWish = groupItems(p.wishlist_items);
  return (
    <div className="space-y-5 max-w-5xl mx-auto">
      {/* Profile Header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#fff7e8] to-white border-2 border-[#7649e6]/20 shadow-cartoon-lg">
        <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#ffd1e8] rounded-full floaty opacity-40"/>
        <div className="absolute -bottom-20 -left-16 w-56 h-56 bg-[#c8efff] rounded-full floaty-2 opacity-40"/>
        {/* Color accent bar */}
        <div className="h-20" style={{ background: `linear-gradient(135deg, ${primaryColor}22, ${accentColor}22)` }}/>
        <div className="relative px-6 pb-5" style={{ marginTop: '-3rem' }}>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="shrink-0">
              <div className="w-24 h-24 rounded-2xl shadow-cartoon" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`, padding: 4 }}>
                {p.avatar_url ? <img src={p.avatar_url} className="w-full h-full rounded-xl object-cover border-3 border-white"/> : <div className="w-full h-full rounded-xl bg-white flex items-center justify-center"><UserRound className="w-10 h-10" style={{ color: primaryColor }}/></div>}
              </div>
            </div>
            <div className="flex-1 min-w-0 pt-2 sm:pt-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <h1 className="font-display text-3xl sm:text-4xl text-[#2a1f3d] truncate">{p.roblox_username}</h1>
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#7649e6] text-white capitalize text-xs">{p.role || "user"}</Badge>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#c8efff]/50 text-[11px] text-[#2a1f3d]/70"><Clock className="w-3 h-3"/>Joined {createdDate}</div>
                  <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium text-white`} style={{ backgroundColor: STATUS_CONFIG[userStatus]?.color || "#22c55e" }}>
                    <StatusIcon className="w-3 h-3"/>{STATUS_CONFIG[userStatus]?.label || "Online"}
                  </div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div className="flex items-center gap-1">{renderStars(p.average_rating || 0, "w-4 h-4")}<span className="font-bold text-base ml-1 text-[#2a1f3d]">{(p.average_rating || 0).toFixed(1)}</span></div>
                <span className="text-xs text-[#7649e6] font-medium">{p.review_count || 0} review{(p.review_count || 0) !== 1 ? 's' : ''}</span>
              </div>
              {p.bio && <p className="mt-2 text-sm text-[#2a1f3d]/70 max-w-lg leading-relaxed">{p.bio}</p>}
            </div>
            <div className="flex sm:flex-col gap-2 shrink-0">
              {isMe && <Button data-testid="edit-profile" onClick={() => setEditing(true)} className="rounded-xl bg-[#7649e6] hover:bg-[#5d35c4] shadow-cartoon-sm btn-pop text-sm h-9"><Pencil className="w-3.5 h-3.5 mr-1.5"/>Edit</Button>}
              {!isMe && user && <ReportDialog target={p.roblox_username} trigger={<Button variant="outline" className="rounded-xl border-[#7649e6]/30 text-[#7649e6] hover:bg-[#7649e6]/5 btn-pop text-sm h-9"><Flag className="w-3.5 h-3.5 mr-1.5"/>Report</Button>}/>}
            </div>
          </div>
          {/* Rank Badge */}
          <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${rank.color} text-white shadow-cartoon-sm`}>
            <RankIcon className="w-4 h-4"/>
            <span className="font-display text-sm">{p.trading_rank || "New Trader"}</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card-cartoon p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#ff6fa3]/15 flex items-center justify-center shrink-0"><ShoppingBag className="w-5 h-5 text-[#ff6fa3]"/></div>
          <div><div className="font-display text-2xl text-[#2a1f3d]">{p.trades_completed || 0}</div><div className="text-[11px] font-semibold text-[#7649e6] uppercase tracking-wide">Completed</div></div>
        </div>
        <div className="card-cartoon p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#36a86b]/15 flex items-center justify-center shrink-0"><TrendingUp className="w-5 h-5 text-[#36a86b]"/></div>
          <div><div className="font-display text-2xl text-[#2a1f3d]">{p.success_rate || 0}%</div><div className="text-[11px] font-semibold text-[#7649e6] uppercase tracking-wide">Success Rate</div></div>
        </div>
        <div className="card-cartoon p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#ffb13b]/15 flex items-center justify-center shrink-0"><Star className="w-5 h-5 text-[#ffb13b]"/></div>
          <div><div className="font-display text-2xl text-[#2a1f3d]">{(p.average_rating || 0).toFixed(1)}</div><div className="text-[11px] font-semibold text-[#7649e6] uppercase tracking-wide">Avg Rating</div></div>
        </div>
        <div className="card-cartoon p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#3aa8ff]/15 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-[#3aa8ff]"/></div>
          <div><div className="font-display text-2xl text-[#2a1f3d]">{invValue.toFixed(0)}</div><div className="text-[11px] font-semibold text-[#7649e6] uppercase tracking-wide">Inv Value</div></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card-cartoon p-1">
        <div className="flex gap-1 bg-[#fff1a8]/30 rounded-2xl p-1.5 mx-3 mt-3">
          <button onClick={() => setTab("reviews")} className={`flex-1 px-4 py-2 text-sm font-semibold rounded-xl transition-all ${tab === "reviews" ? "bg-white shadow-sm text-[#7649e6]" : "text-[#2a1f3d]/60 hover:text-[#2a1f3d]/80"}`}><Star className="w-4 h-4 inline mr-1.5 -mt-0.5"/>Reviews ({reviewTotal})</button>
          <button onClick={() => setTab("inventory")} className={`flex-1 px-4 py-2 text-sm font-semibold rounded-xl transition-all ${tab === "inventory" ? "bg-white shadow-sm text-[#7649e6]" : "text-[#2a1f3d]/60 hover:text-[#2a1f3d]/80"}`}><ShoppingBag className="w-4 h-4 inline mr-1.5 -mt-0.5"/>Inventory ({groupedInv.length})</button>
          <button onClick={() => setTab("wishlist")} className={`flex-1 px-4 py-2 text-sm font-semibold rounded-xl transition-all ${tab === "wishlist" ? "bg-white shadow-sm text-[#7649e6]" : "text-[#2a1f3d]/60 hover:text-[#2a1f3d]/80"}`}><Sparkles className="w-4 h-4 inline mr-1.5 -mt-0.5"/>Wishlist ({groupedWish.length})</button>
        </div>

        <div className="p-4">
          {tab === "reviews" && (
            reviews.length === 0 ? (
              <div className="text-center py-12"><Star className="w-12 h-12 mx-auto text-gray-300"/><p className="mt-3 text-sm text-gray-500">No reviews yet.</p></div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-auto pr-1 scrollbar-thin">
                {reviews.map(r => (
                  <div key={r.id} className="p-4 rounded-2xl bg-gradient-to-br from-white to-[#fff1a8]/20 border border-[#7649e6]/10 hover:shadow-cartoon-sm transition-shadow">
                    <div className="flex items-start gap-3">
                      {r.reviewer_avatar ? <img src={r.reviewer_avatar} className="w-10 h-10 rounded-full border-2 border-[#ff6fa3]/30 shrink-0"/> : <div className="w-10 h-10 rounded-full bg-[#ffd1e8] flex items-center justify-center shrink-0"><UserRound className="w-5 h-5 text-[#ff6fa3]"/></div>}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/profile/${r.reviewer_username}`} className="font-bold text-sm text-[#2a1f3d] hover:underline truncate">{r.reviewer_username}</Link>
                          <div className="flex items-center">{renderStars(r.rating, "w-3.5 h-3.5")}</div>
                          <Badge className="bg-[#36a86b]/15 text-[#36a86b] text-[10px] border-0 font-semibold"><CheckCircle className="w-3 h-3 mr-0.5"/>Verified</Badge>
                        </div>
                        {r.tags && r.tags.length > 0 && <div className="flex gap-1 mt-1.5 flex-wrap">{r.tags.map(t => <span key={t} className="text-[11px] px-2.5 py-0.5 rounded-full bg-[#c8efff]/40 text-[#2a1f3d]/70 font-medium">{QUICK_TAGS.find(qt => qt.value === t)?.label || t}</span>)}</div>}
                        {r.text && <p className="text-sm mt-1.5 text-[#2a1f3d]/70 leading-relaxed">{r.text}</p>}
                        <div className="text-[11px] text-gray-400 mt-1">{new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
          {tab === "inventory" && (
            (!p.inventory_items || p.inventory_items.length === 0) ? (
              <div className="text-center py-12"><ShoppingBag className="w-12 h-12 mx-auto text-gray-300"/><p className="mt-3 text-sm text-gray-500">Inventory is empty.</p></div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {groupedInv.map((i, k) => <ItemCard key={k} item={i} variant={i.variant || "normal"} badge={i.quantity > 1 ? i.quantity : null} compact/>)}
              </div>
            )
          )}
          {tab === "wishlist" && (
            (!p.wishlist_items || p.wishlist_items.length === 0) ? (
              <div className="text-center py-12"><Sparkles className="w-12 h-12 mx-auto text-gray-300"/><p className="mt-3 text-sm text-gray-500">Wishlist is empty.</p></div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {groupedWish.map((i, k) => <ItemCard key={k} item={i} variant={i.variant || "normal"} badge={i.quantity > 1 ? i.quantity : null} compact/>)}
              </div>
            )
          )}
        </div>
      </div>

      {isMe && editing && <ProfileEditor profile={p} onClose={() => { setEditing(false); load(); }}/>}
    </div>
  );
}

function ProfileEditor({ profile, onClose }) {
  const [bio, setBio] = useState(profile.bio || "");
  const [inv, setInv] = useState(profile.inventory || []);
  const [wish, setWish] = useState(profile.wishlist || []);
  const [invItems, setInvItems] = useState(profile.inventory_items || []);
  const [wishItems, setWishItems] = useState(profile.wishlist_items || []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [target, setTarget] = useState("inv");
  const [status, setStatus] = useState(profile.status || "online");
  const [primaryColor, setPrimaryColor] = useState((profile.profile_colors || {}).primary || "#ff6fa3");
  const [accentColor, setAccentColor] = useState((profile.profile_colors || {}).accent || "#c279ff");
  const THEMES = [
    { name: "Pink", primary: "#ff6fa3", accent: "#c279ff" },
    { name: "Purple", primary: "#7649e6", accent: "#3aa8ff" },
    { name: "Green", primary: "#36a86b", accent: "#2d8f5e" },
    { name: "Orange", primary: "#ffb13b", accent: "#f59f1f" },
    { name: "Blue", primary: "#3aa8ff", accent: "#1a7fd1" },
    { name: "Red", primary: "#ef4444", accent: "#dc2626" },
    { name: "Teal", primary: "#14b8a6", accent: "#0d9488" },
    { name: "Rose", primary: "#f43f5e", accent: "#e11d48" },
  ];
  const save = async () => {
    try {
      const r = await api.put("/profile", { bio, inventory: inv, wishlist: wish, status, profile_colors: { primary: primaryColor, accent: accentColor } });
      const stored = JSON.parse(localStorage.getItem("adopt_user") || "{}");
      localStorage.setItem("adopt_user", JSON.stringify({ ...stored, ...r.data }));
      toast.success("Profile saved"); playSound("success"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); playSound("error"); }
  };
  const addItem = (it) => {
    const qty = it.quantity || 1;
    const entry = { id: it.id, variant: it.variant || "normal", potion: it.potion || "none", quantity: qty };
    const displayItem = { ...it, variant: entry.variant, potion: entry.potion, quantity: qty };
    if (target === "inv") { setInv([...inv, entry]); setInvItems([...invItems, displayItem]); }
    else { setWish([...wish, entry]); setWishItems([...wishItems, displayItem]); }
  };
  const removeItem = (kind, idx) => {
    if (kind === "inv") { setInv(inv.filter((_, k) => k !== idx)); setInvItems(invItems.filter((_, k) => k !== idx)); }
    else { setWish(wish.filter((_, k) => k !== idx)); setWishItems(wishItems.filter((_, k) => k !== idx)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl bg-white border-4 border-[#7649e6]/30 max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle className="font-display text-3xl text-[#7649e6]">Edit your profile</DialogTitle></DialogHeader>
        <div className="space-y-5">
          {/* Status */}
          <div>
            <label className="text-sm font-semibold block mb-2">Status</label>
            <div className="flex gap-2">
              {[
                { value: "online", icon: CheckCircle, label: "Online", color: "#22c55e" },
                { value: "idle", icon: Clock, label: "Idle", color: "#eab308" },
                { value: "dnd", icon: X, label: "Do Not Disturb", color: "#ef4444" },
              ].map(s => {
                const Icon = s.icon;
                return <button key={s.value} onClick={() => setStatus(s.value)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all border-2 ${status === s.value ? "border-current shadow-sm" : "border-transparent opacity-60 hover:opacity-100"}`}
                  style={{ color: s.color, background: `${s.color}15` }}>
                  <Icon className="w-4 h-4"/>{s.label}
                </button>;
              })}
            </div>
          </div>
          {/* Profile Theme */}
          <div>
            <label className="text-sm font-semibold block mb-2">Profile Theme</label>
            <div className="flex gap-2 flex-wrap">
              {THEMES.map(t => (
                <button key={t.name} onClick={() => { setPrimaryColor(t.primary); setAccentColor(t.accent); }}
                  className={`w-10 h-10 rounded-xl border-2 transition-all ${primaryColor === t.primary && accentColor === t.accent ? "border-[#2a1f3d] shadow-md scale-110" : "border-transparent hover:scale-105"}`}
                  style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.accent})` }}
                  title={t.name}/>
              ))}
            </div>
            {/* Custom color inputs */}
            <div className="flex gap-3 mt-3">
              <div className="flex-1">
                <label className="text-xs text-[#7649e6] font-medium">Primary</label>
                <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="w-full h-8 rounded-lg cursor-pointer border border-gray-200"/>
              </div>
              <div className="flex-1">
                <label className="text-xs text-[#7649e6] font-medium">Accent</label>
                <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)} className="w-full h-8 rounded-lg cursor-pointer border border-gray-200"/>
              </div>
            </div>
          </div>
          {/* Bio */}
          <div>
            <label className="text-sm font-semibold block mb-1">Bio (no links or scam keywords)</label>
            <Textarea data-testid="profile-bio" value={bio} onChange={e=>setBio(e.target.value)} maxLength={300} className="rounded-2xl"/>
          </div>
          {/* Inventory */}
          <div>
            <div className="flex justify-between items-center mb-1"><span className="font-display text-lg">Inventory ({invItems.length})</span><Button data-testid="add-inventory" onClick={() => { setTarget("inv"); setPickerOpen(true); }} variant="outline" size="sm" className="rounded-2xl"><Plus className="w-4 h-4 mr-1"/>Add</Button></div>
            <div className="grid grid-cols-5 sm:grid-cols-7 gap-2">
              {invItems.map((i, k) => <div key={k} className="relative"><ItemCard item={i} variant={i.variant || "normal"} compact/><button onClick={() => removeItem("inv", k)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"><X className="w-3 h-3"/></button></div>)}
            </div>
          </div>
          {/* Wishlist */}
          <div>
            <div className="flex justify-between items-center mb-1"><span className="font-display text-lg">Wishlist ({wishItems.length})</span><Button data-testid="add-wishlist" onClick={() => { setTarget("wish"); setPickerOpen(true); }} variant="outline" size="sm" className="rounded-2xl"><Plus className="w-4 h-4 mr-1"/>Add</Button></div>
            <div className="grid grid-cols-5 sm:grid-cols-7 gap-2">
              {wishItems.map((i, k) => <div key={k} className="relative"><ItemCard item={i} variant={i.variant || "normal"} compact/><button onClick={() => removeItem("wish", k)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"><X className="w-3 h-3"/></button></div>)}
            </div>
          </div>
        </div>
        <DialogFooter className="mt-5"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button data-testid="save-profile" onClick={save} className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a]">Save</Button></DialogFooter>
        <ItemPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} onPick={addItem}/>
      </DialogContent>
    </Dialog>
  );
}

// =================================================================================
// Support (AI bot + ticket escalation)
// =================================================================================
function Support() {
  const user = getUser();
  const [tab, setTab] = useState("ai");
  const [aiMsgs, setAiMsgs] = useState([{ role: "bot", text: "Hi! I'm Trader Pup. Ask me anything about Adopt Me values, trades, or how this site works. If I can't help, hit \"Need a real person\" to open a ticket with our team." }]);
  const [aiInput, setAiInput] = useState(""); const [aiLoading, setAiLoading] = useState(false); const [sessionId] = useState(crypto.randomUUID());
  const [tickets, setTickets] = useState([]); const [active, setActive] = useState(null); const [tMsgs, setTMsgs] = useState([]);
  const [ticketInput, setTicketInput] = useState(""); const [newSubject, setNewSubject] = useState("");

  const sendAI = async () => {
    if (!aiInput.trim()) return;
    const m = aiInput; setAiInput(""); setAiMsgs(p => [...p, { role: "user", text: m }]); setAiLoading(true);
    try { const r = await api.post("/assistant/chat", { message: m, session_id: sessionId }); setAiMsgs(p => [...p, { role: "bot", text: r.data.reply }]); playSound("ding"); }
    catch (e) { setAiMsgs(p => [...p, { role: "bot", text: "Sorry, I'm having trouble right now. Try again or open a human ticket." }]); }
    finally { setAiLoading(false); }
  };
  const loadTickets = async () => { try { const r = await api.get("/support/tickets"); setTickets(r.data.tickets); } catch {} };
  useEffect(() => { if (user) loadTickets(); }, []);
  const loadTicket = async (id) => { try { const r = await api.get(`/support/tickets/${id}`); setActive(r.data); setTMsgs(r.data.messages || []); } catch {} };
  const createTicket = async () => {
    if (!newSubject.trim()) return;
    try { const r = await api.post("/support/tickets", { subject: newSubject, initial_message: aiMsgs.filter(x => x.role === "user").map(x => x.text).join("\n") || "Need help" });
      toast.success("Ticket created! Staff will reply soon."); setNewSubject(""); loadTickets(); loadTicket(r.data.id); setTab("ticket"); playSound("success");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const sendTicket = async () => {
    if (!ticketInput.trim() || !active) return;
    try { const r = await api.post(`/support/tickets/${active.id}/messages`, { content: ticketInput }); setTMsgs(p => [...p, r.data]); setTicketInput(""); if (r.data.flagged) toast.error("Message blocked by scam shield"); else playSound("pop"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const closeTicket = async () => { if (!active) return; try { await api.post(`/support/tickets/${active.id}/close`); toast.success("Ticket closed"); loadTickets(); } catch {} setActive(null); setTab("ai"); };

  return (
    <div className="space-y-4">
      <div className="card-cartoon p-5 bg-gradient-to-br from-[#c8efff]/40 to-[#fff1a8]/40">
        <h1 className="font-display text-4xl text-[#7649e6] flex items-center gap-3"><LifeBuoy className="w-10 h-10"/>Support</h1>
        <p className="text-sm">Chat with Trader Pup (AI) anytime. If you need a real person, open a ticket and our team will reply.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-2xl bg-[#fff1a8]/40 flex-wrap h-auto">
          <TabsTrigger value="ai" data-testid="support-tab-ai"><Bot className="w-4 h-4 mr-1"/>AI Assistant</TabsTrigger>
          {user && <TabsTrigger value="ticket" data-testid="support-tab-ticket"><UserRound className="w-4 h-4 mr-1"/>My Tickets ({tickets.length})</TabsTrigger>}
        </TabsList>
        <TabsContent value="ai">
          <div className="card-cartoon p-4 flex flex-col h-[400px] sm:h-[500px]">
            <div className="flex-1 overflow-auto scrollbar-thin space-y-2 pr-1">
              {aiMsgs.map((m, i) => (
                <div key={i} className={`p-3 rounded-2xl max-w-[80%] ${m.role === "user" ? "bg-[#fff1a8]/60 ml-auto" : "bg-[#c8efff]/60 mr-auto"}`}>
                  <div className="text-[10px] font-bold uppercase flex items-center gap-1">{m.role === "user" ? "You" : <><Bot className="w-3 h-3"/>Trader Pup</>}</div>
                  <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                </div>
              ))}
              {aiLoading && <div className="text-sm text-gray-500 animate-pulse">Trader Pup is typing…</div>}
            </div>
            {user ? (<>
              <div className="flex gap-2 mt-2"><Input data-testid="ai-input" value={aiInput} onChange={e=>setAiInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendAI()} placeholder="Ask anything…" className="rounded-2xl"/><Button data-testid="ai-send" onClick={sendAI} disabled={aiLoading} className="rounded-2xl bg-[#7649e6] hover:bg-[#5d35c4]"><Send className="w-4 h-4"/></Button></div>
              <div className="mt-3 flex gap-2 items-center flex-wrap">
                <Input data-testid="ticket-subject" value={newSubject} onChange={e=>setNewSubject(e.target.value)} placeholder="Need a real person? Describe your issue here…" className="rounded-2xl flex-1 min-w-48"/>
                <Button data-testid="create-ticket" onClick={createTicket} disabled={!newSubject.trim()} className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a] shadow-cartoon-sm"><LifeBuoy className="w-4 h-4 mr-1"/>Need a real person</Button>
              </div>
            </>) : <div className="mt-2 text-sm text-gray-500">Verify with Roblox to chat with our AI or staff.</div>}
          </div>
        </TabsContent>
        {user && <TabsContent value="ticket">
          <div className="grid md:grid-cols-[280px_1fr] gap-4 responsive-stack">
            <div className="card-cartoon p-3 max-h-[200px] md:max-h-[500px] overflow-auto">
              {tickets.length === 0 && <div className="text-sm text-gray-500 text-center py-6">No tickets yet</div>}
              {tickets.map(t => (
                <button key={t.id} data-testid={`ticket-${t.id}`} onClick={() => loadTicket(t.id)} className={`w-full text-left p-2 rounded-xl mb-1 ${active?.id === t.id ? "bg-[#ff6fa3]/20 border-2 border-[#ff6fa3]" : "hover:bg-[#fff1a8]/40"}`}>
                  <div className="text-xs flex justify-between"><b className="truncate">{t.subject}</b><Badge className={t.status === "closed" ? "bg-gray-400" : t.status === "in_progress" ? "bg-[#ffb13b]" : "bg-[#36a86b]"}>{t.status}</Badge></div>
                  <div className="text-[10px] text-[#7649e6]">{new Date(t.updated_at).toLocaleString()}</div>
                </button>
              ))}
            </div>
            <div className="card-cartoon p-4 flex flex-col h-[500px]">
              {!active ? <div className="text-center text-gray-500 m-auto">Select a ticket to view</div> : (<>
                <div className="flex justify-between items-center mb-2"><div><div className="font-display text-xl">{active.subject}</div><div className="text-xs text-[#7649e6]">Status: {active.status}</div></div>{active.status !== "closed" && <Button size="sm" variant="outline" onClick={closeTicket} className="rounded-2xl"><CheckCircle className="w-3 h-3 mr-1"/>Close</Button>}</div>
                <div className="flex-1 overflow-auto space-y-2 pr-1">
                  {tMsgs.map(m => (
                    <div key={m.id} className={`p-2 rounded-2xl ${m.flagged ? "bg-red-100 border-2 border-red-400" : m.sender_id === user?.id ? "bg-[#fff1a8]/60 ml-8" : "bg-[#c8efff]/60 mr-8"}`}>
                      <div className="text-xs font-bold flex items-center gap-1">{m.sender_avatar && <img src={m.sender_avatar} className="w-4 h-4 rounded-full"/>}{m.sender_username} {m.sender_role !== "user" && <Crown className="w-3 h-3 text-[#ffb13b]"/>}</div>
                      <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                    </div>
                  ))}
                </div>
                {active.status !== "closed" && <div className="flex gap-2 mt-2"><Input data-testid="ticket-reply" value={ticketInput} onChange={e=>setTicketInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendTicket()} placeholder="Type a reply…" className="rounded-2xl"/><Button data-testid="ticket-send" onClick={sendTicket} className="rounded-2xl bg-[#7649e6] hover:bg-[#5d35c4]"><Send className="w-4 h-4"/></Button></div>}
              </>)}
            </div>
          </div>
        </TabsContent>}
      </Tabs>
    </div>
  );
}

// =================================================================================
// Admin Panel with Ads Management
// =================================================================================
function Partnerships() {
  const [partners, setPartners] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/partnerships").then(r => setPartners(r.data.partnerships)).catch(()=>{}).finally(() => setLoading(false));
  }, []);
  return (
    <div className="space-y-4">
      <div className="card-cartoon p-4 sm:p-5 bg-gradient-to-br from-[#d4fff0]/40 to-[#c8efff]/40">
        <div className="flex items-center gap-3 mb-2">
          <Handshake className="w-8 h-8 text-[#36a86b]"/>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-[#36a86b]">Partners</h1>
            <p className="text-xs sm:text-sm">Trusted communities we work with</p>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="grid gap-3">{[1,2,3].map(i => <div key={i} className="card-cartoon p-4 animate-pulse"><div className="flex items-center gap-3"><div className="w-14 h-14 rounded-2xl bg-[#d4fff0]/60"/><div className="flex-1"><div className="h-4 bg-[#d4fff0]/60 rounded w-32 mb-1"/><div className="h-3 bg-[#d4fff0]/40 rounded w-48"/></div></div></div>)}</div>
      ) : partners.length === 0 ? (
        <div className="card-cartoon p-8 text-center text-gray-500"><Handshake className="w-12 h-12 mx-auto mb-3 text-[#36a86b]/30"/><p className="font-display text-xl">No partners yet</p><p className="text-sm">Check back soon!</p></div>
      ) : (
        <div className="grid gap-3">
          {partners.map(p => (
            <div key={p.id} className="card-cartoon overflow-hidden transition-all">
              <button onClick={() => setExpanded(expanded === p.id ? null : p.id)} className="w-full text-left p-4 flex items-center gap-3 hover:bg-[#d4fff0]/20 transition-colors">
                {p.image_url && <img src={p.image_url} className="w-14 h-14 rounded-2xl object-cover border-2 border-[#36a86b]/20 shrink-0"/>}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg text-[#36a86b]">{p.name}</div>
                  <div className="text-xs text-gray-500 line-clamp-2">{p.description}</div>
                </div>
                <div className="shrink-0 text-[#36a86b]">{expanded === p.id ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</div>
              </button>
              {expanded === p.id && (
                <div className="px-4 pb-4 pt-0 border-t-2 border-[#36a86b]/10 animate-in slide-in-from-top-1">
                  <div className="mt-3 p-4 rounded-2xl bg-gradient-to-br from-[#d4fff0]/30 to-white border-2 border-[#36a86b]/20 whitespace-pre-line text-sm leading-relaxed">
                    {p.promotion || p.description}
                  </div>
                  {p.invite_url && (
                    <a href={p.invite_url} target="_blank" rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-[#5865F2] hover:bg-[#4752c4] text-white text-sm font-semibold btn-pop transition-all">
                      <ExternalLink className="w-4 h-4"/>Join Discord
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Admin() {
  const user = getUser();
  const [stats, setStats] = useState(null); const [users, setUsers] = useState([]); const [logs, setLogs] = useState([]); const [reports, setReports] = useState([]); const [ads, setAds] = useState([]);
  const [tickets, setTickets] = useState([]); const [activeTicket, setActiveTicket] = useState(null); const [tMsgs, setTMsgs] = useState([]); const [tReply, setTReply] = useState("");
  const [roles, setRoles] = useState([]); const [allPerms, setAllPerms] = useState([]);
  const [bugReports, setBugReports] = useState([]);
  const [partners, setPartners] = useState([]);
  const [items, setItems] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [itemTypeFilter, setItemTypeFilter] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [showItemEditor, setShowItemEditor] = useState(false);
  const loadItems = () => api.get("/items", { params: { limit: 2000 } }).then(r => setItems(r.data.items)).catch(()=>{});
  const reloadUsers = () => api.get("/admin/users").then(r => setUsers(r.data.users)).catch(()=>{});
  const reloadAds = () => api.get("/admin/ads").then(r => setAds(r.data.ads)).catch(()=>{});
  const reloadTickets = () => api.get("/admin/support").then(r => setTickets(r.data.tickets)).catch(()=>{});
  const reloadRoles = () => api.get("/admin/roles").then(r => setRoles(r.data.roles)).catch(()=>{});
  const reloadBugs = () => api.get("/admin/bug-reports").then(r => setBugReports(r.data.reports)).catch(()=>{});
  const reloadPartners = () => api.get("/admin/partnerships").then(r => setPartners(r.data.partnerships)).catch(()=>{});
  useEffect(() => {
    api.get("/admin/stats").then(r => setStats(r.data)).catch(()=>{});
    reloadUsers();
    api.get("/admin/moderation").then(r => setLogs(r.data.logs)).catch(()=>{});
    api.get("/admin/reports").then(r => setReports(r.data.reports)).catch(()=>{});
    reloadAds();
    reloadTickets();
    reloadRoles();
    reloadBugs();
    reloadPartners();
    loadItems();
    api.get("/admin/permissions").then(r => setAllPerms(r.data.permissions)).catch(()=>{});
  }, []);
  const loadTicket = async (id) => { try { const r = await api.get(`/support/tickets/${id}`); setActiveTicket(r.data); setTMsgs(r.data.messages || []); } catch {} };
  const replyTicket = async () => {
    if (!tReply.trim() || !activeTicket) return;
    try { const r = await api.post(`/support/tickets/${activeTicket.id}/messages`, { content: tReply }); setTMsgs(p => [...p, r.data]); setTReply(""); playSound("pop"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  if (!user || !["owner","admin","moderator","helper","staff"].includes(user.role)) return <div className="card-cartoon p-8 text-center">Admin only</div>;
  const isAdminPlus = ["owner","admin"].includes(user.role);
  const isOwner = user.role === "owner";
  return (
    <div className="space-y-5">
      <h1 className="font-display text-4xl text-[#ff5a8a]">Owner Control Panel</h1>
      {stats && <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{Object.entries(stats).map(([k,v]) => <div key={k} className="card-cartoon p-4 text-center"><div className="text-xs uppercase text-[#7649e6]">{k.replace(/_/g, ' ')}</div><div className="font-display text-3xl">{v}</div></div>)}</div>}
      <Tabs defaultValue="users">
        <TabsList className="rounded-2xl bg-[#fff1a8]/40 flex-nowrap overflow-x-auto scrollbar-thin h-auto w-full sm:flex-wrap sm:overflow-visible">
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="mod" data-testid="admin-tab-mod">Moderation</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="support" data-testid="admin-tab-support"><LifeBuoy className="w-4 h-4 mr-1"/>Support ({tickets.filter(t => t.status !== "closed").length})</TabsTrigger>
          {isOwner && <TabsTrigger value="roles" data-testid="admin-tab-roles"><Crown className="w-4 h-4 mr-1"/>Roles & Permissions</TabsTrigger>}
          {isAdminPlus && <TabsTrigger value="ads" data-testid="admin-tab-ads"><Megaphone className="w-4 h-4 mr-1"/>Ads</TabsTrigger>}
          {isAdminPlus && <TabsTrigger value="partners" data-testid="admin-tab-partners"><Handshake className="w-4 h-4 mr-1"/>Partners</TabsTrigger>}
          {isAdminPlus && <TabsTrigger value="values" data-testid="admin-tab-values"><Sparkles className="w-4 h-4 mr-1"/>Values</TabsTrigger>}
          {isAdminPlus && <TabsTrigger value="bugs" data-testid="admin-tab-bugs"><Bug className="w-4 h-4 mr-1"/>Bug Reports ({bugReports.length})</TabsTrigger>}
        </TabsList>
        <TabsContent value="users"><div className="card-cartoon p-3">
          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <span className="font-display text-lg">Find a player</span>
            <PlayerSearch/>
            <span className="text-xs text-[#7649e6]">Search by Roblox username, click to open profile</span>
          </div>
          <div className="responsive-table"><table className="w-full text-sm"><thead><tr><th className="text-left p-2">User</th><th>Role</th><th>Rep</th><th>Status</th><th>Actions</th></tr></thead><tbody>{users.map(u => <tr key={u.id} className="border-t border-[#2a1f3d]/10"><td className="p-2 flex items-center gap-2 whitespace-nowrap">{u.avatar_url && <img src={u.avatar_url} className="w-6 h-6 rounded-full shrink-0"/>}<span className="truncate max-w-[100px] sm:max-w-none">{u.roblox_username}</span></td><td className="text-center"><Badge className="capitalize bg-[#7649e6] whitespace-nowrap">{u.role}</Badge></td><td className="text-center">{u.reputation}</td><td className="text-center">{u.banned ? <Badge className="bg-red-500 whitespace-nowrap">Banned</Badge> : "OK"}</td><td className="text-center space-x-1 whitespace-nowrap">{user.role === "owner" && <select data-testid={`role-select-${u.id}`} defaultValue={u.role} onChange={async (e) => { try { await api.post(`/admin/users/${u.id}/role`, { role: e.target.value }); toast.success("Role updated"); reloadUsers(); } catch {} }} className="rounded text-xs"><option>user</option><option>helper</option><option>staff</option><option>moderator</option><option>admin</option><option>owner</option></select>}<Button data-testid={`ban-${u.id}`} size="sm" variant="destructive" className="rounded-xl" onClick={async () => { try { await api.post(`/admin/users/${u.id}/${u.banned ? "unban" : "ban"}`); toast.success(u.banned ? "Unbanned" : "Banned"); reloadUsers(); } catch {} }}>{u.banned ? "Unban" : "Ban"}</Button></td></tr>)}</tbody></table></div></div></TabsContent>
        <TabsContent value="mod"><div className="card-cartoon p-3 space-y-2 max-h-96 overflow-auto">{logs.map(l => <div key={l.id} className="p-2 border-b border-[#2a1f3d]/10"><div className="flex justify-between text-xs"><b>{l.username}</b><span>{new Date(l.created_at).toLocaleString()}</span></div><div className="text-sm text-red-600">{l.reason} ({l.method})</div><div className="text-xs italic">"{l.content}"</div></div>)}{logs.length === 0 && <div className="text-center text-gray-500 py-6">No flagged messages</div>}</div></TabsContent>
        <TabsContent value="reports"><div className="card-cartoon p-3 space-y-2 max-h-96 overflow-auto">{reports.map(r => <div key={r.id} className="p-2 border-b border-[#2a1f3d]/10 flex items-start gap-2"><div className="flex-1"><div className="text-xs"><b>{r.reporter_username}</b> reported <b className="text-red-600">{r.target_username}</b></div><div className="text-sm">{r.reason}</div></div>{r.trade_id && <><Link data-testid={`view-chat-${r.id}`} to={`/trades/${r.trade_id}`} className="rounded-xl bg-[#7649e6] text-white px-3 py-1 text-xs btn-pop flex items-center gap-1"><Eye className="w-3 h-3"/>View chat</Link><Button size="sm" data-testid={`delete-trade-${r.id}`} variant="destructive" className="rounded-xl" onClick={async () => { if (!window.confirm("Delete this trade?")) return; try { await api.delete(`/trades/${r.trade_id}`); toast.success("Trade deleted"); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } }}><Trash2 className="w-3 h-3"/></Button></>}</div>)}{reports.length === 0 && <div className="text-center text-gray-500 py-6">No reports</div>}</div></TabsContent>
        {isOwner && <TabsContent value="roles"><RolesManager roles={roles} allPerms={allPerms} onReload={reloadRoles} users={users} onReloadUsers={reloadUsers}/></TabsContent>}
        {isAdminPlus && <TabsContent value="ads"><AdsManager ads={ads} onReload={reloadAds}/></TabsContent>}
        {isAdminPlus && <TabsContent value="partners"><PartnershipsManager partners={partners} onReload={reloadPartners}/></TabsContent>}
        <TabsContent value="bugs">
          <div className="card-cartoon p-3 space-y-2 max-h-96 overflow-auto">
            {bugReports.length === 0 && <div className="text-center text-gray-500 py-6">No bug reports</div>}
            {bugReports.map(b => (
              <div key={b.id} className="p-3 rounded-2xl border-2 border-[#2a1f3d]/10">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm">{b.subject}</span>
                      <Badge className={b.status === "open" ? "bg-yellow-500" : "bg-[#36a86b]"}>{b.status}</Badge>
                    </div>
                    <p className="text-sm text-[#2a1f3d]/70 mt-1 whitespace-pre-wrap">{b.description}</p>
                    <div className="text-xs text-[#7649e6] mt-1">
                      By {b.username} • {new Date(b.created_at).toLocaleString()}
                      {b.page_url && <> • <a href={b.page_url} target="_blank" rel="noreferrer" className="underline">Page</a></>}
                    </div>
                    {b.screenshot && (
                      <div className="mt-2">
                        <img
                          src={b.screenshot}
                          alt="Bug screenshot"
                          className="max-h-48 rounded-xl border border-[#2a1f3d]/10 cursor-pointer object-contain bg-black/5 hover:opacity-90 transition-opacity"
                          onClick={() => window.open(b.screenshot, "_blank")}
                        />
                      </div>
                    )}
                  </div>
                    {b.status === "open" && (
                      <Button size="sm" data-testid={`resolve-bug-${b.id}`} onClick={async () => {
                        try { await api.post(`/admin/bug-reports/${b.id}/resolve`); toast.success("Bug report marked as resolved"); } catch {}
                        reloadBugs();
                      }} className="rounded-xl bg-[#36a86b] hover:bg-[#2d8f5e] text-white shrink-0"><CheckCircle className="w-3 h-3 mr-1"/>Resolve</Button>
                    )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="values">
          <div className="card-cartoon p-6 space-y-4">
            <h3 className="font-display text-2xl flex items-center gap-2"><Sparkles className="text-[#ffb13b]"/>Pet Values</h3>
            <p className="text-sm text-[#7649e6]">Upload a <code>values.txt</code> file, auto-sync from adoptmevalues.app, or edit items individually.</p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="cursor-pointer btn-pop">
                <input type="file" accept=".txt" onChange={async (e) => {
                  const f = e.target.files[0]; if (!f) return;
                  const fd = new FormData(); fd.append("file", f);
                  try { const r = await api.post("/admin/refresh-values", fd); toast.success(`${r.data.count} items loaded`); playSound("success"); loadItems(); }
                  catch (ex) { toast.error(ex.response?.data?.detail || "Refresh failed"); playSound("error"); }
                  e.target.value = "";
                }} className="hidden"/>
                <span className="inline-flex items-center rounded-2xl bg-[#7649e6] text-white px-4 py-2 shadow-cartoon-sm"><Upload className="w-4 h-4 mr-2"/>Upload values.txt</span>
              </label>
              <Button onClick={async () => {
                try { const r = await api.post("/admin/refresh-values"); toast.success(`${r.data.count} items refreshed`); playSound("success"); loadItems(); }
                catch (ex) { toast.error(ex.response?.data?.detail || "Refresh failed"); playSound("error"); }
              }} className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a]"><Sparkles className="w-4 h-4 mr-2"/>Refresh from file</Button>
              <Button onClick={async () => {
                try { const r = await api.post("/admin/sync-values"); toast.success(`${r.data.count} items synced from adoptmevalues.app`); playSound("success"); loadItems(); }
                catch (ex) { console.error("Sync error:", ex); const msg = ex.response?.data?.detail || ex.message || "Sync failed"; toast.error(msg); playSound("error"); }
              }} className="rounded-2xl bg-[#36a86b] hover:bg-[#2d8f5e]"><RefreshCw className="w-4 h-4 mr-2"/>Sync from adoptmevalues.app</Button>
            </div>
            {items.length > 0 && <p className="text-xs text-gray-500">{items.length} items currently loaded</p>}
          </div>
          {items.length > 0 && <div className="card-cartoon p-6 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display text-lg flex items-center gap-2"><Search className="w-4 h-4"/>Item Editor</h4>
              <div className="flex gap-2">
                <Input placeholder="Search items…" value={itemSearch} onChange={e => setItemSearch(e.target.value)} className="rounded-2xl w-48 text-sm"/>
                <select value={itemTypeFilter} onChange={e => setItemTypeFilter(e.target.value)} className="rounded-2xl bg-white border px-2 py-1 text-sm">
                  <option value="">All types</option>
                  <option value="pets">Pets</option>
                  <option value="vehicles">Vehicles</option>
                  <option value="toys">Toys</option>
                  <option value="pet wear">Pet Wear</option>
                  <option value="strollers">Strollers</option>
                  <option value="eggs">Eggs</option>
                  <option value="gifts">Gifts</option>
                  <option value="food">Food</option>
                  <option value="stickers">Stickers</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="max-h-[400px] overflow-auto rounded-2xl border">
              <table className="w-full text-xs">
                <thead><tr className="bg-[#fff1a8]/40 sticky top-0">
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Rarity</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-right">Value</th>
                  <th className="p-2 text-right">Neon</th>
                  <th className="p-2 text-right">Mega</th>
                  <th className="p-2 text-center">Actions</th>
                </tr></thead>
                <tbody>{items.filter(i => {
                  if (itemSearch && !i.name.toLowerCase().includes(itemSearch.toLowerCase())) return false;
                  if (itemTypeFilter && i.type !== itemTypeFilter) return false;
                  return true;
                }).slice(0, 100).map(i => (
                  <tr key={i.id} className="border-t hover:bg-[#c8efff]/20">
                    <td className="p-2 font-medium">{i.name}</td>
                    <td className="p-2 text-gray-600">{i.rarity}</td>
                    <td className="p-2 text-gray-600">{i.type}</td>
                    <td className="p-2 text-right">{i.value}</td>
                    <td className="p-2 text-right">{i.value_neon}</td>
                    <td className="p-2 text-right">{i.value_mega}</td>
                    <td className="p-2 text-center">
                      <Button size="sm" className="rounded-2xl bg-[#7649e6]/80 hover:bg-[#7649e6] text-xs px-2 py-1"
                        onClick={() => { setEditItem(i); setShowItemEditor(true); }}>Edit</Button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>}
          {showItemEditor && editItem && <ItemEditorModal item={editItem} onClose={() => setShowItemEditor(false)} onSaved={() => { setShowItemEditor(false); loadItems(); }} />}
        </TabsContent>
        <TabsContent value="support">
          <div className="grid md:grid-cols-[300px_1fr] gap-4 responsive-stack">
            <div className="card-cartoon p-3 max-h-[200px] md:max-h-[500px] overflow-auto">
              {tickets.length === 0 && <div className="text-sm text-gray-500 text-center py-6">No tickets yet</div>}
              {tickets.map(t => (
                <button key={t.id} data-testid={`admin-ticket-${t.id}`} onClick={() => loadTicket(t.id)} className={`w-full text-left p-2 rounded-xl mb-1 ${activeTicket?.id === t.id ? "bg-[#ff6fa3]/20 border-2 border-[#ff6fa3]" : "hover:bg-[#fff1a8]/40"}`}>
                  <div className="flex items-center gap-2"><img src={t.avatar_url} className="w-6 h-6 rounded-full"/><div className="flex-1 min-w-0"><div className="text-xs flex justify-between"><b className="truncate">{t.username}</b><Badge className={t.status === "closed" ? "bg-gray-400" : t.status === "in_progress" ? "bg-[#ffb13b]" : "bg-[#36a86b]"}>{t.status}</Badge></div><div className="text-xs truncate">{t.subject}</div></div></div>
                </button>
              ))}
            </div>
            <div className="card-cartoon p-4 flex flex-col h-[400px] sm:h-[500px]">
              {!activeTicket ? <div className="text-center text-gray-500 m-auto">Select a ticket to reply</div> : (<>
                <div className="font-display text-xl mb-2 flex items-center gap-2 flex-wrap">{activeTicket.subject} <Badge className="ml-2 bg-[#7649e6]">{activeTicket.username}</Badge>
                  {activeTicket.status !== "closed" ? <Button data-testid="admin-close-ticket" size="sm" onClick={async () => {
                    try { await api.post(`/support/tickets/${activeTicket.id}/close`); toast.success("Ticket closed"); playSound("success"); reloadTickets(); setActiveTicket({...activeTicket, status: "closed"}); }
                    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
                  }} className="ml-auto rounded-2xl bg-red-500 hover:bg-red-600"><X className="w-4 h-4 mr-1"/>Close</Button> : <Button data-testid="admin-reopen-ticket" size="sm" onClick={async () => {
                    try { await api.post(`/support/tickets/${activeTicket.id}/reopen`); toast.success("Ticket reopened"); playSound("ding"); reloadTickets(); setActiveTicket({...activeTicket, status: "open"}); }
                    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
                  }} className="ml-auto rounded-2xl bg-[#36a86b] hover:bg-[#2d8f5e]"><CheckCircle className="w-4 h-4 mr-1"/>Reopen</Button>}
                </div>
                <div className="flex-1 overflow-auto space-y-2 pr-1">
                  {tMsgs.map(m => (
                    <div key={m.id} className={`p-2 rounded-2xl ${m.flagged ? "bg-red-100" : m.sender_id === user?.id ? "bg-[#fff1a8]/60 ml-8" : "bg-[#c8efff]/60 mr-8"}`}>
                      <div className="text-xs font-bold flex items-center gap-1">{m.sender_username}{m.sender_role !== "user" && <Crown className="w-3 h-3 text-[#ffb13b]"/>}</div>
                      <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2"><Input data-testid="admin-ticket-reply" value={tReply} onChange={e=>setTReply(e.target.value)} onKeyDown={e => e.key === "Enter" && replyTicket()} placeholder="Reply as staff…" className="rounded-2xl"/><Button data-testid="admin-ticket-send" onClick={replyTicket} className="rounded-2xl bg-[#7649e6] hover:bg-[#5d35c4]"><Send className="w-4 h-4"/></Button></div>
              </>)}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AdsManager({ ads, onReload }) {
  const [form, setForm] = useState({ title: "", image_url: "", link_url: "", sponsor: "", placement: "home", active: true, cost: 0 });
  const save = async () => {
    try { await api.post("/admin/ads", form); toast.success("Ad saved"); setForm({ title: "", image_url: "", link_url: "", sponsor: "", placement: "home", active: true, cost: 0 }); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { if (!window.confirm("Delete ad?")) return; try { await api.delete(`/admin/ads/${id}`); onReload(); } catch {} };
  const toggle = async (a) => { try { await api.post("/admin/ads", { ...a, active: !a.active }); onReload(); } catch {} };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="card-cartoon p-4">
        <h3 className="font-display text-2xl mb-3">New Sponsor Ad</h3>
        <div className="space-y-2">
          <Input data-testid="ad-title" placeholder="Title" value={form.title} onChange={e=>setForm({...form, title: e.target.value})} className="rounded-2xl"/>
          <Input data-testid="ad-sponsor" placeholder="Sponsor name" value={form.sponsor} onChange={e=>setForm({...form, sponsor: e.target.value})} className="rounded-2xl"/>
          <Input data-testid="ad-image" placeholder="Image URL" value={form.image_url} onChange={e=>setForm({...form, image_url: e.target.value})} className="rounded-2xl"/>
          <Input data-testid="ad-link" placeholder="Click-through URL" value={form.link_url} onChange={e=>setForm({...form, link_url: e.target.value})} className="rounded-2xl"/>
          <Select value={form.placement} onValueChange={(v)=>setForm({...form, placement: v})}>
            <SelectTrigger data-testid="ad-placement" className="rounded-2xl"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="home">Home (above features)</SelectItem><SelectItem value="home_bottom">Home (bottom)</SelectItem><SelectItem value="trades_top">Trade Center (top)</SelectItem><SelectItem value="calculator_top">Calculator (top)</SelectItem><SelectItem value="scanner_top">Scanner (top)</SelectItem></SelectContent>
          </Select>
          <Input data-testid="ad-cost" type="number" placeholder="Cost (USD)" value={form.cost} onChange={e=>setForm({...form, cost: parseFloat(e.target.value)||0})} className="rounded-2xl"/>
          <Button data-testid="save-ad" onClick={save} disabled={!form.title} className="w-full rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a]">Save Ad</Button>
        </div>
      </div>
      <div className="card-cartoon p-4">
        <h3 className="font-display text-2xl mb-3">Existing Ads ({ads.length})</h3>
        <div className="space-y-2 max-h-96 overflow-auto">
          {ads.map(a => (
            <div key={a.id} className="p-2 border-2 border-[#2a1f3d]/10 rounded-2xl flex items-center gap-2">
              {a.image_url && <img src={a.image_url} className="w-12 h-12 rounded-xl object-cover"/>}
              <div className="flex-1 min-w-0"><div className="font-bold truncate">{a.title}</div><div className="text-xs text-[#7649e6]">{a.sponsor} • {a.placement} • ${a.cost}</div></div>
              <Button size="sm" variant="outline" data-testid={`toggle-ad-${a.id}`} onClick={()=>toggle(a)} className="rounded-xl">{a.active ? "Pause" : "Activate"}</Button>
              <Button size="sm" variant="destructive" data-testid={`delete-ad-${a.id}`} onClick={()=>del(a.id)} className="rounded-xl"><Trash2 className="w-3 h-3"/></Button>
            </div>
          ))}
          {ads.length === 0 && <div className="text-sm text-gray-500 text-center py-6">No ads yet</div>}
        </div>
      </div>
    </div>
  );
}

function PartnershipsManager({ partners, onReload }) {
  const [form, setForm] = useState({ name: "", image_url: "", invite_url: "", description: "", promotion: "", active: true, sort_order: 0 });
  const save = async () => {
    try { await api.post("/admin/partnerships", form); toast.success("Partner saved"); setForm({ name: "", image_url: "", invite_url: "", description: "", promotion: "", active: true, sort_order: 0 }); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { if (!window.confirm("Delete partner?")) return; try { await api.delete(`/admin/partnerships/${id}`); onReload(); } catch {} };
  const toggle = async (p) => { try { await api.post("/admin/partnerships", { ...p, active: !p.active }); onReload(); } catch {} };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="card-cartoon p-4">
        <h3 className="font-display text-2xl mb-3">New Partner Ad</h3>
        <div className="space-y-2">
          <Input placeholder="Server/Community name" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} className="rounded-2xl"/>
          <Input placeholder="Image URL (logo/banner)" value={form.image_url} onChange={e=>setForm({...form, image_url: e.target.value})} className="rounded-2xl"/>
          <Input placeholder="Discord invite URL" value={form.invite_url} onChange={e=>setForm({...form, invite_url: e.target.value})} className="rounded-2xl"/>
          <Textarea placeholder="Short description (shown collapsed)" value={form.description} onChange={e=>setForm({...form, description: e.target.value})} className="rounded-2xl"/>
          <Textarea placeholder="Full promotion text (shown expanded)" value={form.promotion} onChange={e=>setForm({...form, promotion: e.target.value})} className="rounded-2xl min-h-[200px]"/>
          <Input type="number" placeholder="Sort order (lower = first)" value={form.sort_order} onChange={e=>setForm({...form, sort_order: parseInt(e.target.value)||0})} className="rounded-2xl"/>
          <Button onClick={save} disabled={!form.name} className="w-full rounded-2xl bg-[#36a86b] hover:bg-[#2d8f5e]">Save Partner</Button>
        </div>
      </div>
      <div className="card-cartoon p-4">
        <h3 className="font-display text-2xl mb-3">Existing Partners ({partners.length})</h3>
        <div className="space-y-2 max-h-96 overflow-auto">
          {partners.map(p => (
            <div key={p.id} className="p-2 border-2 border-[#2a1f3d]/10 rounded-2xl flex items-center gap-2">
              {p.image_url && <img src={p.image_url} className="w-12 h-12 rounded-xl object-cover"/>}
              <div className="flex-1 min-w-0"><div className="font-bold truncate">{p.name}</div><div className="text-xs text-[#36a86b]">Order: {p.sort_order}</div></div>
              <Button size="sm" variant="outline" onClick={()=>toggle(p)} className="rounded-xl">{p.active ? "Pause" : "Activate"}</Button>
              <Button size="sm" variant="destructive" onClick={()=>del(p.id)} className="rounded-xl"><Trash2 className="w-3 h-3"/></Button>
            </div>
          ))}
          {partners.length === 0 && <div className="text-sm text-gray-500 text-center py-6">No partners yet</div>}
        </div>
      </div>
    </div>
  );
}

function RolesManager({ roles, allPerms, onReload, users, onReloadUsers }) {
  const [editing, setEditing] = useState(null); // role being edited
  const [newRole, setNewRole] = useState({ name: "", color: "#7649e6", rank: 1, permissions: [] });
  const [userPerms, setUserPerms] = useState(null); // {user, custom_permissions}

  const togglePerm = (state, setState, perm) => {
    const arr = state.permissions || [];
    setState({ ...state, permissions: arr.includes(perm) ? arr.filter(p => p !== perm) : [...arr, perm] });
  };
  const saveRole = async (role) => {
    try { await api.post("/admin/roles", { name: role.name, color: role.color, rank: role.rank, permissions: role.permissions });
      toast.success(`Role "${role.name}" saved`); playSound("success"); setEditing(null); setNewRole({ name: "", color: "#7649e6", rank: 1, permissions: [] }); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const deleteRole = async (name) => {
    if (!window.confirm(`Delete role "${name}"? Users with this role will be reset to "user".`)) return;
    try { await api.delete(`/admin/roles/${name}`); toast.success("Deleted"); onReload(); onReloadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const saveUserPerms = async () => {
    if (!userPerms) return;
    try { await api.put(`/admin/users/${userPerms.user.id}/permissions`, { custom_permissions: userPerms.permissions });
      toast.success("User permissions saved"); onReloadUsers(); setUserPerms(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const PermGrid = ({ state, setState, disabled }) => (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
      {allPerms.map(p => (
        <label key={p} className={`flex items-center gap-2 p-2 rounded-xl border-2 cursor-pointer text-sm ${(state.permissions || []).includes(p) ? "border-[#ff6fa3] bg-[#ffd1e8]/40" : "border-[#2a1f3d]/10 bg-white hover:bg-[#fff1a8]/30"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
          <input data-testid={`perm-${p}`} type="checkbox" disabled={disabled} checked={(state.permissions || []).includes(p)} onChange={() => !disabled && togglePerm(state, setState, p)} className="w-4 h-4 accent-[#ff6fa3]"/>
          <span className="font-mono text-xs">{p}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="card-cartoon p-4">
        <h3 className="font-display text-2xl mb-3 flex items-center gap-2"><Crown className="text-[#ffb13b]"/>Existing Roles</h3>
        <div className="space-y-2">
          {roles.map(r => (
            <div key={r.name} data-testid={`role-row-${r.name}`} className="p-3 rounded-2xl border-2 border-[#2a1f3d]/10">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge style={{backgroundColor: r.color}} className="text-white capitalize px-3 py-1">{r.name}</Badge>
                <span className="text-xs text-[#7649e6]">rank {r.rank} • {(r.permissions || []).length} perms</span>
                <div className="ml-auto flex gap-2">
                  <Button data-testid={`edit-role-${r.name}`} size="sm" variant="outline" className="rounded-xl" onClick={() => setEditing(editing?.name === r.name ? null : { ...r })}>{editing?.name === r.name ? "Close" : "Edit perms"}</Button>
                  {!r.system && r.name !== "owner" && <Button data-testid={`delete-role-${r.name}`} size="sm" variant="destructive" className="rounded-xl" onClick={() => deleteRole(r.name)}><Trash2 className="w-3 h-3"/></Button>}
                </div>
              </div>
              {editing?.name === r.name && (
                <div className="mt-3">
                  <div className="flex gap-2 items-center mb-2">
                    <label className="text-xs">Color:</label>
                    <input type="color" value={editing.color} onChange={e => setEditing({...editing, color: e.target.value})} className="w-12 h-8 rounded"/>
                    <label className="text-xs">Rank:</label>
                    <Input type="number" value={editing.rank} onChange={e => setEditing({...editing, rank: parseInt(e.target.value)||0})} className="w-20 rounded-xl"/>
                  </div>
                  <PermGrid state={editing} setState={setEditing} disabled={r.name === "owner"}/>
                  {r.name === "owner" && <p className="text-xs text-[#7649e6] mt-2">Owner always has every permission.</p>}
                  <Button data-testid={`save-role-${r.name}`} onClick={() => saveRole(editing)} className="mt-3 rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a]">Save</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card-cartoon p-4">
        <h3 className="font-display text-2xl mb-3">Create New Role</h3>
        <div className="flex gap-2 items-center flex-wrap mb-2">
          <Input data-testid="new-role-name" value={newRole.name} onChange={e => setNewRole({...newRole, name: e.target.value})} placeholder="role-name (e.g. trade-checker)" className="rounded-xl flex-1 min-w-48"/>
          <input type="color" value={newRole.color} onChange={e => setNewRole({...newRole, color: e.target.value})} className="w-12 h-10 rounded"/>
          <Input type="number" value={newRole.rank} onChange={e => setNewRole({...newRole, rank: parseInt(e.target.value)||1})} placeholder="rank" className="w-24 rounded-xl"/>
        </div>
        <PermGrid state={newRole} setState={setNewRole}/>
        <Button data-testid="create-role" onClick={() => saveRole(newRole)} disabled={!newRole.name.trim()} className="mt-3 rounded-2xl bg-[#7649e6] hover:bg-[#5d35c4]">Create Role</Button>
      </div>

      <div className="card-cartoon p-4">
        <h3 className="font-display text-2xl mb-3">Per-user Permission Overrides</h3>
        <p className="text-xs text-[#7649e6] mb-2">Grant extra permissions to a specific user without changing their role.</p>
        <div className="space-y-1 max-h-60 overflow-auto">
          {users.map(u => (
            <button key={u.id} data-testid={`user-perms-${u.id}`} onClick={() => setUserPerms({ user: u, permissions: u.custom_permissions || [] })} className={`w-full text-left p-2 rounded-xl flex items-center gap-2 ${userPerms?.user.id === u.id ? "bg-[#ff6fa3]/20 border-2 border-[#ff6fa3]" : "hover:bg-[#fff1a8]/30 border-2 border-transparent"}`}>
              {u.avatar_url && <img src={u.avatar_url} className="w-6 h-6 rounded-full"/>}
              <span className="font-bold">{u.roblox_username}</span>
              <Badge className="bg-[#7649e6] capitalize ml-2">{u.role}</Badge>
              {(u.custom_permissions || []).length > 0 && <Badge className="bg-[#ffb13b] ml-1">+{u.custom_permissions.length} custom</Badge>}
            </button>
          ))}
        </div>
        {userPerms && (
          <div className="mt-3 pt-3 border-t-2 border-[#2a1f3d]/10">
            <div className="font-bold">Editing: {userPerms.user.roblox_username}</div>
            <PermGrid state={userPerms} setState={setUserPerms}/>
            <Button data-testid="save-user-perms" onClick={saveUserPerms} className="mt-3 rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a]">Save overrides</Button>
          </div>
        )}
      </div>
    </div>
  );
}

// =================================================================================
// Item Editor Modal
// =================================================================================
function ItemEditorModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState({ ...item });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        id: form.id,
        name: form.name,
        image: form.image || "",
        rarity: form.rarity,
        type: form.type,
        category: form.category || "",
        value: parseFloat(form.value) || 0,
        value_neon: parseFloat(form.value_neon) || 0,
        value_mega: parseFloat(form.value_mega) || 0,
        score: parseInt(form.score) || 9999,
      };
      await api.post("/admin/items", payload);
      toast.success("Item saved");
      playSound("success");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      playSound("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-cartoon p-6 max-w-lg w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <h3 className="font-display text-2xl mb-4 flex items-center gap-2"><Pencil className="w-5 h-5 text-[#ffb13b]"/>Edit Item</h3>
        <div className="space-y-3">
          <div><label className="text-xs font-bold text-[#7649e6]">Name</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-bold text-[#7649e6]">Rarity</label>
              <select value={form.rarity} onChange={e => setForm({...form, rarity: e.target.value})} className="w-full rounded-xl border px-3 py-2 text-sm">
                {["legendary","ultra rare","rare","uncommon","common"].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-bold text-[#7649e6]">Type</label>
              <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full rounded-xl border px-3 py-2 text-sm">
                {["pets","vehicles","toys","pet wear","strollers","eggs","gifts","food","stickers","other"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="text-xs font-bold text-[#7649e6]">Value</label><Input type="number" step="0.01" value={form.value} onChange={e => setForm({...form, value: e.target.value})} className="rounded-xl"/></div>
            <div><label className="text-xs font-bold text-[#7649e6]">Neon</label><Input type="number" step="0.01" value={form.value_neon} onChange={e => setForm({...form, value_neon: e.target.value})} className="rounded-xl"/></div>
            <div><label className="text-xs font-bold text-[#7649e6]">Mega</label><Input type="number" step="0.01" value={form.value_mega} onChange={e => setForm({...form, value_mega: e.target.value})} className="rounded-xl"/></div>
          </div>
          <div><label className="text-xs font-bold text-[#7649e6]">Score (demand, lower = higher demand)</label><Input type="number" value={form.score} onChange={e => setForm({...form, score: e.target.value})} className="rounded-xl"/></div>
          <div><label className="text-xs font-bold text-[#7649e6]">Image URL</label><Input value={form.image || ""} onChange={e => setForm({...form, image: e.target.value})} className="rounded-xl"/></div>
          <div><label className="text-xs font-bold text-[#7649e6]">Category</label><Input value={form.category || ""} onChange={e => setForm({...form, category: e.target.value})} className="rounded-xl"/></div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button onClick={onClose} className="rounded-2xl bg-gray-400 hover:bg-gray-500">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-2xl bg-[#7649e6] hover:bg-[#5d35c4]">{saving ? <Loader2 className="w-4 h-4 animate-spin"/> : null}Save</Button>
        </div>
      </div>
    </div>
  );
}

// =================================================================================
// App Root
// =================================================================================
function BioWarning() {
  const [state, setState] = useState({ show: false, banned: false, reason: "", bioContent: "", timeLeft: 120 });
  const [clearing, setClearing] = useState(false);
  const playRef = useRef(false);
  const nav = useNavigate();
  const banEndRef = useRef(null);

  useEffect(() => {
    const poll = () => {
      const u = getUser();
      if (!u) { setState(s => ({ ...s, show: false, banned: false })); return; }
      api.get("/user/bio-flag").then(r => {
        const f = !!r.data.bio_flagged;
        const b = !!r.data.banned;
        if (f && !playRef.current) { playSound("ban"); playRef.current = true; }
        if (!f) { playRef.current = false; banEndRef.current = null; }
        if (f && !b && banEndRef.current === null) {
          banEndRef.current = Date.now() + 120000;
        }
        setState(s => ({
          ...s,
          show: f && !b,
          banned: b,
          reason: r.data.ban_reason || r.data.reason || "",
          bioContent: r.data.bio_content || "",
        }));
      }).catch(() => { setState(s => ({ ...s, show: false, banned: false })); });
    };
    const tick = () => {
      if (banEndRef.current !== null) {
        const remaining = Math.max(0, (banEndRef.current - Date.now()) / 1000);
        setState(s => s.show ? { ...s, timeLeft: remaining } : s);
        if (remaining <= 0) {
          banEndRef.current = null;
          setState(s => ({ ...s, timeLeft: 0, banned: true, show: false }));
          poll();
        }
      }
    };
    poll();
    const pollTimer = setInterval(poll, 5000);
    const tickTimer = setInterval(tick, 200);
    return () => { clearInterval(pollTimer); clearInterval(tickTimer); };
  }, []);

  const handleClear = async () => {
    setClearing(true);
    try {
      await api.post("/user/clear-bio-flag");
      setState(s => ({ ...s, show: false, banned: false, timeLeft: 120 }));
      banEndRef.current = null;
      toast.success("Bio flag cleared! Welcome back.");
      playSound("success");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Your bio still contains flagged content. Please edit it.");
    } finally { setClearing(false); }
  };

  const { show, banned, reason, bioContent, timeLeft } = state;
  const fm = Math.floor(timeLeft / 60);
  const fs = Math.floor(timeLeft % 60);
  const timerStr = `${fm}:${fs.toString().padStart(2, "0")}`;
  const urgency = timeLeft < 30 ? "text-red-400" : timeLeft < 60 ? "text-orange-400" : "text-yellow-300";

  if (banned) {
    return <BanOverlay reason={reason} />;
  }

  if (show !== true) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 bio-warning-overlay">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(circle at 50% 50%, rgba(220,38,38,0.15) 0%, transparent 70%)",
      }} />
      <div className="absolute top-0 left-0 right-0 h-2 bio-warning-badge" />
      <div className="absolute bottom-0 left-0 right-0 h-2 bio-warning-badge" />

      <div className="bio-warning-card relative bg-[#1a0a0a] border-4 border-red-600 rounded-3xl p-8 max-w-lg w-full text-center space-y-5 shadow-2xl shadow-red-900/50">
        <div className="mx-auto">
          <ShieldAlert className="w-16 h-16 text-red-500 mx-auto" style={{ filter: "drop-shadow(0 0 20px rgba(220,38,38,0.5))" }} />
        </div>

        <h1 className="font-display text-4xl text-red-500 uppercase tracking-widest" style={{ textShadow: "0 0 20px rgba(220,38,38,0.5)" }}>
          Bio Flagged
        </h1>

        <p className="text-gray-300 text-sm">
          Your Roblox profile contains content that <span className="text-red-400 font-bold">violates our rules</span>:
        </p>

        <div className="bio-warning-badge rounded-xl px-4 py-3 text-white font-bold text-sm">
          <AlertTriangle className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          {reason || "Suspicious / fraudulent content detected"}
        </div>

        {bioContent && (
          <div className="bg-black/60 rounded-xl px-4 py-3 border border-red-800/50">
            <div className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Your current bio:</div>
            <div className="text-gray-200 text-sm font-mono break-words max-h-24 overflow-y-auto">
              {bioContent}
            </div>
          </div>
        )}

        <p className="text-gray-400 text-xs">
          Remove it from your <strong className="text-white">Roblox profile bio</strong> within <strong className="text-red-400">2 minutes</strong>, then click the button below.
        </p>

        <div className="bg-black/60 rounded-2xl p-4 border border-red-800">
          <div className="flex items-center justify-center gap-2 text-gray-500 text-xs uppercase tracking-widest mb-1">
            <Clock className="w-3.5 h-3.5" />
            Auto-ban in
          </div>
          <div className={`bio-countdown font-display text-5xl font-bold ${urgency}`}>
            {timerStr}
          </div>
          <div className="text-gray-600 text-[10px] mt-1">after this time, only Support will be accessible</div>
          <div className="mt-3 h-2 bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full bg-red-600 rounded-full transition-all duration-1000" style={{ width: `${(timeLeft / 120) * 100}%` }} />
          </div>
        </div>

        <div className="flex flex-col gap-2.5 mt-2">
        <a href="https://www.roblox.com/users/profile/edit" target="_blank" rel="noopener noreferrer"
           className="rounded-2xl bg-red-700 hover:bg-red-600 text-white font-bold py-3 px-4 text-center transition-all hover:scale-105 shadow-lg shadow-red-900/30 inline-flex items-center justify-center gap-2 w-full">
          <Pencil className="w-4 h-4" />
          Edit my Roblox bio
        </a>
          <Button onClick={handleClear} disabled={clearing}
            className="rounded-2xl bg-[#36a86b] hover:bg-[#2d8f5e] shadow-cartoon btn-pop text-white font-bold py-3 inline-flex items-center justify-center gap-2">
            {clearing ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Checking...</>
            ) : (
              <><CheckCircle className="w-4 h-4" /> I've removed the content</>
            )}
          </Button>
          <p className="text-[10px] text-gray-600">
            Changed your mind? <a href="/support" onClick={() => window.location.href = "/support"} className="text-gray-400 hover:underline">Contact support</a>
          </p>
        </div>
      </div>
    </div>
  );
}

function BanOverlay({ reason }) {
  const location = useLocation();
  const safePaths = ["/support", "/admin", "/profile"];

  useEffect(() => { playSound("ban"); }, []);

  if (safePaths.some(p => location.pathname.startsWith(p))) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4 bio-ban-overlay">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at center, rgba(127,29,29,0.2) 0%, transparent 60%)",
      }} />
      <div className="absolute top-0 left-0 right-0 h-1 bio-warning-badge" />

      <div className="relative text-center space-y-6 max-w-lg">
        <div className="mx-auto">
          <Lock className="w-20 h-20 text-red-500 mx-auto" style={{ filter: "drop-shadow(0 0 30px rgba(220,38,38,0.5))" }} />
        </div>

        <h1 className="font-display text-5xl text-red-500 uppercase tracking-[0.2em]" style={{ textShadow: "0 0 30px rgba(220,38,38,0.4)" }}>
          Account Banned
        </h1>

        <div className="h-px bg-gradient-to-r from-transparent via-red-500 to-transparent" />

        <p className="text-gray-400 text-sm leading-relaxed">
          Your account has been <span className="text-red-400 font-bold">restricted</span>.
          You no longer have access to most features.
        </p>

        <div className="bg-red-900/20 border border-red-800/50 rounded-2xl px-5 py-4">
          <div className="flex items-center justify-center gap-2 text-red-300 text-xs font-mono mb-1">
            <Flag className="w-3.5 h-3.5" />
            Reason
          </div>
          <p className="text-red-200 text-sm font-bold">{reason || "Violation of terms of service"}</p>
        </div>

          <a href="https://www.roblox.com/users/profile/edit" target="_blank" rel="noopener noreferrer"
             className="rounded-2xl bg-red-700 hover:bg-red-600 text-white font-bold py-3 px-4 text-center transition-all hover:scale-105 shadow-lg shadow-red-900/30 inline-flex items-center justify-center gap-2">
            <Pencil className="w-4 h-4" />
            Edit my Roblox bio now
          </a>

        <div className="bg-[#1a0a0a] border border-gray-800 rounded-2xl px-5 py-4 space-y-2">
          <p className="text-gray-300 text-sm font-bold">Think this was a mistake?</p>
          <p className="text-gray-500 text-xs leading-relaxed">
            If you believe this was an error or if you wish to appeal, please create a support
            ticket. Our staff will review your case and can reinstate your account if everything
            is in order.
          </p>
        </div>

        <div className="flex flex-col gap-3 pt-2">
          <a href="/support" onClick={() => window.location.href = "/support"}
             className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a] text-white font-bold py-3 px-6 transition-all hover:scale-105 shadow-lg shadow-pink-900/30 inline-flex items-center justify-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Contact Support to Appeal
          </a>
          <p className="text-gray-600 text-xs">
            You can only use <a href="/support" onClick={() => window.location.href = "/support"} className="text-gray-400 hover:underline font-bold">Support</a> while banned.
            All other features are locked.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const user = getUser();
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => { document.title = "AdptMeValues"; }, []);

  useEffect(() => {
    if (sessionStorage.getItem("adopt_show_tutorial")) {
      setShowTutorial(true);
    }
  }, []);

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <BioWarning/>
      <Shell>
        <Routes>
          <Route path="/" element={<Home/>}/>
          <Route path="/login" element={<Login/>}/>
          <Route path="/calculator" element={<Calculator/>}/>
          <Route path="/scanner" element={<Protected><Scanner/></Protected>}/>
          <Route path="/trades" element={<TradesList/>}/>
          <Route path="/trades/:id" element={<TradeDetail/>}/>
          <Route path="/messages" element={<Protected><Messages/></Protected>}/>
          <Route path="/profile/:username" element={<Profile/>}/>
          <Route path="/partnerships" element={<Partnerships/>}/>
          <Route path="/support" element={<Support/>}/>
          <Route path="/admin" element={<Protected><Admin/></Protected>}/>
        </Routes>
      </Shell>
      {showTutorial && <TutorialOverlay onComplete={() => { sessionStorage.removeItem("adopt_show_tutorial"); setShowTutorial(false); }}/>}
    </BrowserRouter>
  );
}
